/*
  Поведение витрины IMSAN.

  Один файл на всю тему, без библиотек. В витрине Hydrogen то же самое делал
  React: панели, корзина, подсказки поиска, избранное, галерея, вкладки,
  фильтры. Здесь разметку рисует сервер, а на клиенте остаётся ровно то, что
  без него не работает, — и каждый кусок подписан тем же соображением, что
  и в исходном компоненте.

  Общие правила:
    * ничего не ищем заранее — обработчики висят на документе и смотрят на
      data-атрибуты. Разметку то и дело подменяет Section Rendering API
      (корзина, счётчик), и ссылки на снятые узлы протухают;
    * состояние живёт в разметке (aria-expanded, hidden), а не в переменных:
      после подмены куска сервер отдаёт его вместе с состоянием;
    * ни одна ошибка сети не должна оставлять покупателя с молчащей кнопкой.
*/

(() => {
  'use strict';

  const on = (event, selector, handler) => {
    document.addEventListener(event, (e) => {
      const target = e.target instanceof Element ? e.target.closest(selector) : null;
      if (target) handler(e, target);
    });
  };

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  /* ------------------------------------------------------------ деньги

     Сумма строки на карточке товара считается на клиенте: количество меняют
     кнопками, и цифра в кнопке «в корзину» обязана меняться вместе с ним.
     Формат берём тот же, которым набирает Liquid, иначе одна и та же цена
     на одной странице выглядела бы двумя разными способами. */

  const moneyFormat = document.documentElement.dataset.moneyFormat || '{{amount}} €';

  function formatMoney(cents, format = moneyFormat) {
    const value = Number(cents) || 0;

    const withDelimiters = (amount, precision, thousands, decimal) => {
      const fixed = (amount / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, name) => {
      switch (name) {
        case 'amount':
          return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return withDelimiters(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(value, 0, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'", '.');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  /* --------------------------------------------------- выдвижные панели

     Корзина, поиск, избранное, меню и выбор объекта. Панель — это <div
     class="overlay"> с атрибутом hidden; открытие снимает hidden и вешает
     класс expanded следующим кадром, иначе переход не проигрывается: браузер
     не анимирует свойство, которое изменилось в том же кадре, что и display.

     Пока панель открыта, страница под ней не прокручивается — иначе колесо
     докручивает не список в панели, а каталог за ней. */

  let restoreFocusTo = null;

  function openDrawer(name) {
    const drawer = $(`[data-drawer="${name}"]`);
    if (!drawer) return false;

    restoreFocusTo = document.activeElement;

    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('expanded'));
    document.body.classList.add('overlay-open');

    const focusable = drawer.querySelector('input, button:not(.close-outside), a[href]');
    if (focusable) setTimeout(() => focusable.focus(), 60);

    return true;
  }

  function closeDrawer(drawer) {
    const panel = drawer || $('.overlay.expanded');
    if (!panel) return;

    panel.classList.remove('expanded');
    document.body.classList.remove('overlay-open');

    // hidden ставим после перехода: иначе панель исчезает рывком.
    setTimeout(() => {
      if (!panel.classList.contains('expanded')) panel.hidden = true;
    }, 500);

    if (restoreFocusTo && document.contains(restoreFocusTo)) restoreFocusTo.focus();
    restoreFocusTo = null;
  }

  on('click', '[data-drawer-open]', (event, trigger) => {
    const name = trigger.dataset.drawerOpen;
    // Корзина — обычная ссылка на /cart: без JavaScript она обязана работать
    // переходом, поэтому отменяем его только когда панель действительно есть.
    if (openDrawer(name)) event.preventDefault();
  });

  on('click', '[data-drawer-close]', (event, trigger) => {
    const panel = trigger.closest('.overlay');
    if (!panel) return;
    // Ссылку внутри панели не гасим — переход и должен произойти, панель
    // просто уезжает вместе с уходом со страницы.
    if (trigger.tagName !== 'A') event.preventDefault();
    closeDrawer(panel);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = $('.overlay.expanded');
    if (open) closeDrawer(open);
  });

  /* --------------------------------------------------------------- корзина

     Все правки идут через Ajax API, а разметку после них отдаёт сервер:
     запрашиваем секции корзины и счётчика и подменяем их целиком. Считать
     суммы на клиенте нельзя — у оптовой корзины цена строки зависит от порога
     по объёму, и наша арифметика разошлась бы с тем, что насчитает Shopify. */

  const sectionsUrl = (names) =>
    `${window.location.pathname}?sections=${names.join(',')}`;

  async function refreshCart({open = false} = {}) {
    try {
      const response = await fetch(sectionsUrl(['cart-drawer', 'cart-icon-bubble', 'main-cart']));
      const sections = await response.json();

      const drawer = $('[data-drawer="cart"]');
      if (drawer && sections['cart-drawer']) {
        const wasOpen = drawer.classList.contains('expanded');
        const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const fresh = parsed.querySelector('[data-drawer="cart"]');
        if (fresh) {
          drawer.innerHTML = fresh.innerHTML;
          drawer.hidden = !wasOpen;
          if (wasOpen) drawer.classList.add('expanded');
        }
      }

      const bubble = $('#cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) bubble.innerHTML = sections['cart-icon-bubble'];

      const page = $('[data-cart-page]');
      if (page && sections['main-cart']) {
        const parsed = new DOMParser().parseFromString(sections['main-cart'], 'text/html');
        const fresh = parsed.querySelector('[data-cart-page]');
        if (fresh) page.innerHTML = fresh.innerHTML;
      }

      if (open) openDrawer('cart');
    } catch (error) {
      // Сеть отвалилась — перезагружаем страницу: корзина у Shopify уже
      // изменилась, и молча оставить старую разметку значит соврать.
      console.error('корзина: не удалось обновить разметку', error);
      window.location.reload();
    }
  }

  async function changeCart(body) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body),
    });
    return response.json();
  }

  /* «В корзину» из сетки и с карточки товара. Форму не даём отправить
     страницей: панель должна открыться на месте, а не после перезагрузки. */
  on('submit', 'form[data-cart-add]', async (event, form) => {
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    if (button) button.disabled = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {Accept: 'application/json'},
        body: new FormData(form),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        const note = form.querySelector('[data-cart-error]');
        if (note) {
          note.textContent = error?.description || error?.message || '';
          note.hidden = false;
        }
        return;
      }

      await refreshCart({open: true});
    } finally {
      if (button) button.disabled = false;
    }
  });

  on('click', '[data-cart-quantity]', async (event, button) => {
    event.preventDefault();
    button.disabled = true;
    await changeCart({id: button.dataset.cartQuantity, quantity: Number(button.dataset.quantity)});
    await refreshCart();
  });

  /* Набрать тридцать семь кнопками — это тридцать шесть обращений к Shopify.
     Поэтому число можно вписать: правка уходит по Enter и по уходу из поля. */
  on('change', '[data-cart-quantity-field]', async (event, field) => {
    const quantity = Math.max(0, parseInt(field.value, 10) || 0);
    await changeCart({id: field.dataset.cartQuantityField, quantity});
    await refreshCart();
  });

  on('keydown', '[data-cart-quantity-field]', (event, field) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      field.blur();
    }
  });

  on('click', '[data-cart-clear]', async (event) => {
    event.preventDefault();
    await fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, {method: 'POST'});
    await refreshCart();
  });

  on('click', '[data-cart-note-save]', async (event, button) => {
    event.preventDefault();
    const field = button.closest('[data-cart-panel-body]')?.querySelector('textarea');
    button.disabled = true;
    await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({note: field ? field.value : ''}),
    });
    button.disabled = false;
  });

  /* Примечание и промокод — две кнопки в один ряд, раскрытой всегда не больше
     одной: обе раскрытые добавляли бы к панели два поля, мимо которых каждый
     раз проматывать до кнопки оформления. */
  on('click', '[data-cart-panel]', (event, button) => {
    const summary = button.closest('aside');
    if (!summary) return;
    const name = button.dataset.cartPanel;
    const opening = button.getAttribute('aria-expanded') !== 'true';

    $$('[data-cart-panel]', summary).forEach((other) => {
      other.setAttribute('aria-expanded', 'false');
    });
    $$('[data-cart-panel-body]', summary).forEach((body) => {
      body.hidden = true;
    });

    if (opening) {
      button.setAttribute('aria-expanded', 'true');
      const body = summary.querySelector(`[data-cart-panel-body="${name}"]`);
      if (body) {
        body.hidden = false;
        body.querySelector('textarea, input')?.focus();
      }
    }
  });

  /* Промокод уходит адресом /discount/КОД — штатный путь Shopify: код ложится
     в сессию и доезжает до оформления вместе с корзиной. */
  on('click', '[data-discount-apply]', (event, button) => {
    event.preventDefault();
    const field = button.closest('div')?.querySelector('[data-discount-field]');
    const code = field ? field.value.trim() : '';
    if (!code) return;

    // Адрес приходит с префиксом языка уже собранным: на языке по умолчанию
    // это «/discount/», на прочих — «/en/discount/».
    const base = button.dataset.discountApply || '/discount/';
    const back = `${window.location.pathname}${window.location.search}`;
    window.location.href = `${base}${encodeURIComponent(code)}?redirect=${encodeURIComponent(back)}`;
  });

  /* ------------------------------------------------------ подсказки поиска

     Разметку рисует сервер: /search/suggest с section_id отдаёт готовую
     секцию. Собирать её на клиенте значило бы держать вторую вёрстку тех же
     карточек — и она разошлась бы с первой правкой. */

  let searchTimer = null;
  let searchAbort = null;

  on('input', '[data-predictive-input]', (event, field) => {
    const results = document.querySelector('[data-predictive-results]');
    if (!results) return;

    const term = field.value.trim();
    clearTimeout(searchTimer);

    if (!term) {
      results.innerHTML = '';
      return;
    }

    searchTimer = setTimeout(async () => {
      searchAbort?.abort();
      searchAbort = new AbortController();

      const root = window.Shopify?.routes?.root || '/';
      const url =
        `${root}search/suggest?q=${encodeURIComponent(term)}` +
        '&resources[type]=product,collection&resources[limit]=8' +
        '&section_id=predictive-search';

      try {
        const response = await fetch(url, {signal: searchAbort.signal});
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('.shopify-section');
        results.innerHTML = fresh ? fresh.innerHTML : html;
      } catch (error) {
        if (error.name !== 'AbortError') console.error('поиск: подсказки не пришли', error);
      }
    }, 200);
  });

  /* --------------------------------------------------------- избранное

     Списка желаний у Online Store нет ни в Liquid, ни в Ajax API, а отложить
     товар должен и гость. Поэтому список лежит в хранилище браузера, а панель
     собирается на клиенте — единственное место темы, где вёрстку рисует
     не сервер. */

  const WISHLIST_KEY = 'imsan:wishlist';

  const readWishlist = () => {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const writeWishlist = (items) => {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch {
      /* приватный режим — список просто не переживёт вкладку */
    }
    paintWishlist();
  };

  function wishlistStrings() {
    const node = document.querySelector('[data-wishlist-strings]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  /* Счётчик отдан всеми формами сразу: у русского их четыре, и выбрать нужную
     умеет Intl.PluralRules — имена форм у него те же, что у словаря. Число
     в готовой строке подменяем: в каждой из них ровно одно число. */
  function pluralise(forms, count, locale) {
    if (!forms) return String(count);
    let form = 'other';
    try {
      form = new Intl.PluralRules(locale || 'en').select(count);
    } catch {
      form = count === 1 ? 'one' : 'other';
    }
    const template = forms[form] || forms.other || forms.one || '';
    return template.replace(/\d+/, String(count));
  }

  function paintWishlist() {
    const items = readWishlist();

    // Счётчик в шапке.
    const counter = document.querySelector('[data-wishlist-count]');
    if (counter) {
      counter.hidden = items.length === 0;
      counter.textContent = items.length ? String(items.length) : '';
      counter.className = items.length
        ? 'absolute -right-1.5 -top-1.5 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-accent px-1.5 text-[0.6875rem] font-bold leading-none text-white'
        : '';
    }

    // Залитое сердце на плитках и на карточке товара.
    $$('[data-wishlist-button]').forEach((button) => {
      const active = items.some((item) => item.id === button.dataset.wishlistId);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.classList.toggle('text-accent', active);
      button.classList.toggle('text-muted', !active);
      const path = button.querySelector('svg');
      if (path) path.setAttribute('fill', active ? 'currentColor' : 'none');
    });

    const view = document.querySelector('[data-wishlist-view]');
    if (!view) return;

    const strings = wishlistStrings();
    const empty = view.querySelector('[data-wishlist-empty]');
    const list = view.querySelector('[data-wishlist-list]');
    const signin = view.querySelector('[data-wishlist-signin]');
    const rows = view.querySelector('[data-wishlist-items]');
    const lines = view.querySelector('[data-wishlist-lines]');

    if (empty) empty.hidden = items.length > 0;
    if (list) list.hidden = items.length === 0;
    if (signin) signin.hidden = items.length === 0;
    if (lines && strings) lines.textContent = pluralise(strings.lines, items.length, strings.locale);

    if (!rows) return;
    rows.innerHTML = items
      .map(
        (item) => `
        <li class="flex gap-4 border-b border-rule py-5">
          <a href="${item.url}" data-drawer-close class="h-24 w-24 shrink-0 border border-rule bg-card">
            ${
              item.image
                ? `<img src="${item.image}" alt="" width="96" height="96" loading="lazy" class="h-full w-full object-contain p-2">`
                : ''
            }
          </a>
          <div class="flex min-w-0 flex-1 flex-col">
            <a href="${item.url}" data-drawer-close class="text-sm leading-snug text-ink transition-colors hover:text-ink-strong">${item.title}</a>
            <div class="mt-auto flex items-end justify-between gap-4 pt-3">
              <button type="button" data-wishlist-remove="${item.id}" class="tick text-muted underline decoration-rule underline-offset-4 transition-colors hover:text-ink">
                ${strings ? strings.remove : ''}
              </button>
              <span class="text-sm font-medium text-ink-strong">${item.price}</span>
            </div>
          </div>
        </li>`,
      )
      .join('');
  }

  on('click', '[data-wishlist-button]', (event, button) => {
    event.preventDefault();
    const id = button.dataset.wishlistId;
    const items = readWishlist();
    const next = items.some((item) => item.id === id)
      ? items.filter((item) => item.id !== id)
      : items.concat({
          id,
          url: button.dataset.wishlistUrl,
          title: button.dataset.wishlistTitle,
          image: button.dataset.wishlistImage || '',
          price: button.dataset.wishlistPrice || '',
        });
    writeWishlist(next);
  });

  on('click', '[data-wishlist-remove]', (event, button) => {
    event.preventDefault();
    writeWishlist(readWishlist().filter((item) => item.id !== button.dataset.wishlistRemove));
  });

  on('click', '[data-wishlist-clear]', (event) => {
    event.preventDefault();
    writeWishlist([]);
  });

  /* ------------------------------------------------------- цвета на плитке

     Нажатие на кружок меняет плитку на месте: кадр, артикул, цену и то, что
     уйдёт в корзину. Всё это берётся у одного варианта — того, который Shopify
     отдал для этого значения опции, — иначе плитка показывала бы одну цену,
     а добавляла другую. Повторное нажатие снимает выбор и возвращает вариант
     по умолчанию. */

  on('click', '[data-swatch]', (event, swatch) => {
    event.preventDefault();

    const card = swatch.closest('[data-product-card]');
    if (!card) return;

    const active = swatch.getAttribute('aria-pressed') === 'true';

    /* Плитка обвеса приезжает с сервера уже нажатой на цвете профиля, и снять
       выбор ей некуда: состояния «до», к которому возвращаться, не было.
       Оставляем кружок как есть — иначе он погас бы, а плитка осталась
       в прежнем цвете, и кружки врали бы про то, что уйдёт в корзину. */
    if (active && !card.dataset.cardInitial) return;

    const row = swatch.parentElement;

    $$('[data-swatch]', row).forEach((node) => {
      node.setAttribute('aria-pressed', 'false');
      node.classList.remove('border-night', 'ring-1', 'ring-night', 'ring-offset-1', 'ring-offset-card');
      node.classList.add('border-rule-strong');
    });

    if (active) {
      // Сняли выбор — возвращаем то, с чем плитка приехала с сервера.
      const initial = card.dataset.cardInitial;
      if (initial) applyCardVariant(card, JSON.parse(initial));
      return;
    }

    if (!card.dataset.cardInitial) {
      card.dataset.cardInitial = JSON.stringify(readCardState(card));
    }

    swatch.setAttribute('aria-pressed', 'true');
    swatch.classList.remove('border-rule-strong');
    swatch.classList.add('border-night', 'ring-1', 'ring-night', 'ring-offset-1', 'ring-offset-card');

    applyCardVariant(card, {
      variant: swatch.dataset.swatchVariant,
      url: swatch.dataset.swatchUrl,
      sku: swatch.dataset.swatchSku,
      price: swatch.dataset.swatchPrice,
      priceNet: swatch.dataset.swatchPriceNet,
      image: swatch.dataset.swatchImage,
      srcset: swatch.dataset.swatchSrcset,
    });
  });

  function readCardState(card) {
    return {
      variant: card.querySelector('[data-card-variant]')?.value,
      url: card.querySelector('[data-card-link]')?.getAttribute('href'),
      sku: card.querySelector('[data-card-sku]')?.textContent.trim(),
      price: card.querySelector('[data-card-price]')?.textContent.trim(),
      priceNet: card.querySelector('[data-card-price-net]')?.textContent.trim(),
      image: card.querySelector('[data-card-image]')?.getAttribute('src'),
      srcset: card.querySelector('[data-card-image]')?.getAttribute('srcset'),
    };
  }

  function applyCardVariant(card, state) {
    const image = card.querySelector('[data-card-image]');
    if (image && state.image) {
      image.src = state.image;
      if (state.srcset) image.srcset = state.srcset;
    }

    const sku = card.querySelector('[data-card-sku]');
    if (sku) sku.textContent = state.sku || ' ';

    const price = card.querySelector('[data-card-price]');
    if (price && state.price) price.textContent = state.price;

    // Цена без НДС ходит за ценой: разойдись они, плитка сказала бы,
    // что налог у этого цвета другой.
    const net = card.querySelector('[data-card-price-net]');
    if (net && state.priceNet) net.textContent = state.priceNet;

    const link = card.querySelector('[data-card-link]');
    if (link && state.url) link.href = state.url;

    const field = card.querySelector('[data-card-variant]');
    if (field && state.variant) field.value = state.variant;

    // Отложить надо то, что покупатель видит на плитке, а не первый вариант.
    const heart = card.querySelector('[data-wishlist-button]');
    if (heart) {
      if (state.url) heart.dataset.wishlistUrl = state.url;
      if (state.image) heart.dataset.wishlistImage = state.image;
      if (state.price) heart.dataset.wishlistPrice = state.price;
    }
  }

  /* --------------------------------------------------------- галерея товара

     Крупный кадр и колонка миниатюр. Видео стоит в том же ряду, что и снимки:
     это ещё один взгляд на тот же предмет, и искать его надо там же.

     Выбранный кадр живёт в разметке: открыт тот, у кого снят hidden. Отдельной
     переменной с номером нет нарочно — галерею при смене варианта целиком
     заменяет разметка с сервера, и открытым в ней уже стоит кадр этого
     варианта. Переменная пережила бы подмену и разошлась бы с разметкой. */

  let modelLibrary;
  const modelPlayers = new WeakMap();
  const modelListeners = new WeakSet();

  function applyModelFinish(viewer) {
    const holder = viewer.closest('[data-gallery-model]');
    const finish = JSON.parse(holder?.dataset.modelFinish || 'null');
    if (!finish || !viewer.model) return;
    const material = viewer.model.materials.find((item) => item.name === finish.material);
    if (!material || !/^#[0-9a-f]{6}$/i.test(finish.colour)) return;
    // The scene graph API takes linear RGB; storefront colour codes use sRGB.
    const rgb = finish.colour.slice(1).match(/../g).map((hex) => {
      const channel = parseInt(hex, 16) / 255;
      return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
    material.pbrMetallicRoughness.setBaseColorFactor([...rgb, 1]);
    material.pbrMetallicRoughness.setMetallicFactor(finish.metalness);
    material.pbrMetallicRoughness.setRoughnessFactor(finish.roughness);
    holder.dataset.modelColour = finish.colour;
  }

  function activateModel(node) {
    const viewer = node.querySelector('model-viewer');
    if (!viewer) return;
    if (!modelListeners.has(viewer)) {
      viewer.addEventListener('load', () => applyModelFinish(viewer));
      modelListeners.add(viewer);
    }
    modelLibrary ||= new Promise((resolve, reject) => {
      Shopify.loadFeatures([{name: 'model-viewer-ui', version: '1.0', onLoad: (errors) => {
        if (errors) reject(errors);
        else resolve();
      }}]);
    });
    modelLibrary.then(() => {
      if (!node.isConnected || node.hidden) return;
      if (!modelPlayers.has(viewer)) {
        // A click must not switch the viewer into its disabled poster mode.
        // Remove the attribute: the UI treats even toggleable="false" as true.
        viewer.removeAttribute('toggleable');
        modelPlayers.set(viewer, new Shopify.ModelViewerUI(viewer));
      }
      applyModelFinish(viewer);
      modelPlayers.get(viewer).play();
    }).catch((error) => {
      modelLibrary = null;
      console.error('3D viewer could not be loaded', error);
    });
  }

  function showMedia(gallery, key, reveal) {
    $$('[data-gallery-thumb]', gallery).forEach((node) => {
      const current = node.dataset.galleryThumb === key;
      node.classList.toggle('border-night', current);
      node.classList.toggle('border-rule', !current);
      if (current) node.setAttribute('aria-current', 'true');
      else node.removeAttribute('aria-current');

      /* Ряд миниатюр на телефоне прокручивается вбок, и пролистанный стрелкой
         кадр иначе остался бы отмеченным за краем экрана. На широком экране
         колонка видна целиком и не прокручивается — там не трогаем ничего,
         чтобы не дёргать страницу. */
      if (!current || !reveal) return;
      const list = node.closest('ul');
      if (list && list.scrollWidth > list.clientWidth + 1) {
        node.scrollIntoView({block: 'nearest', inline: 'nearest'});
      }
    });

    $$('[data-gallery-item]', gallery).forEach((node) => {
      const current = node.dataset.galleryItem === key;
      node.hidden = !current;
      // Ролик, уехавший с экрана, продолжать играть незачем.
      if (!current) node.querySelector('video')?.pause();
      if (current) activateModel(node);
      else modelPlayers.get(node.querySelector('model-viewer'))?.pause();
    });
    const showingModel = Boolean($('[data-gallery-item]:not([hidden]) [data-gallery-model]', gallery));
    $$('[data-gallery-step]', gallery).forEach((button) => { button.hidden = showingModel; });
  }

  /* Шаг по кругу: с последнего кадра — на первый. Упереться в край галереи
     из шести снимков и не понять, кончились они или кнопка сломалась, —
     худшее, что может случиться со стрелкой. */
  function stepGallery(gallery, delta) {
    if (!gallery) return;
    // Models open explicitly from their thumbnail. Swiping through photos
    // must not enter a viewer where the same gesture rotates the object.
    const items = $$('[data-gallery-item]', gallery).filter((node) => !node.querySelector('[data-gallery-model]'));
    if (!items.length) return;
    const index = items.findIndex((node) => !node.hidden);
    // Guard against navigation events left over when a model was opened.
    const nextIndex = index < 0 ? (delta > 0 ? 0 : items.length - 1) : (index + delta + items.length) % items.length;
    const next = items[nextIndex];
    showMedia(gallery, next.dataset.galleryItem, true);
  }

  on('click', '[data-gallery-thumb]', (event, thumb) => {
    const gallery = thumb.closest('[data-gallery]');
    if (gallery) showMedia(gallery, thumb.dataset.galleryThumb, false);
  });

  on('click', '[data-gallery-step]', (event, button) => {
    event.preventDefault();
    stepGallery(button.closest('[data-gallery]'), Number(button.dataset.galleryStep));
  });

  /* ------------------------------------------------------ крупный просмотр

     Окно нарисовано вне липкой колонки с галереей — иначе оно уходит под
     шапку, — поэтому связь между кадром и окном только через разметку:
     кнопка несёт адрес снимка, окно помнит, чей кадр показывает. */

  function openLightbox(button) {
    const box = document.querySelector('[data-lightbox]');
    if (!box) return null;

    const image = box.querySelector('img');
    if (image) {
      image.src = button.dataset.zoom;
      image.alt = button.dataset.zoomAlt || '';
    }
    box.dataset.lightboxKey = button.closest('[data-gallery-item]')?.dataset.galleryItem || '';
    box.hidden = false;
    document.body.classList.add('overlay-open');
    return box;
  }

  function closeLightbox() {
    const box = document.querySelector('[data-lightbox]:not([hidden])');
    if (!box) return false;
    box.hidden = true;
    document.body.classList.remove('overlay-open');
    return true;
  }

  /* В окне листаются только снимки: у видео свой проигрыватель, и открывать
     его поверх страницы нечем. Заодно ведём галерею под окном — закрыв его,
     покупатель остаётся на том кадре, который смотрел. */
  function stepLightbox(delta) {
    const box = document.querySelector('[data-lightbox]:not([hidden])');
    const gallery = document.querySelector('[data-gallery]');
    if (!box || !gallery) return;

    const shots = $$('[data-gallery-item]', gallery).filter((node) => node.querySelector('[data-zoom]'));
    if (shots.length < 2) return;

    const index = shots.findIndex((node) => node.dataset.galleryItem === box.dataset.lightboxKey);
    const next = shots[(index + delta + shots.length) % shots.length];
    openLightbox(next.querySelector('[data-zoom]'));
    showMedia(gallery, next.dataset.galleryItem, true);
  }

  /* Кадр открывается крупно по нажатию — не лупой в углу, а самим снимком:
     курсор-лупа говорит об этом раньше, чем значок, который ещё надо заметить. */
  on('click', '[data-zoom]', (event, button) => {
    event.preventDefault();
    const box = openLightbox(button);
    box?.querySelector('[data-lightbox-close]')?.focus();
  });

  on('click', '[data-lightbox-step]', (event, button) => {
    event.preventDefault();
    stepLightbox(Number(button.dataset.lightboxStep));
  });

  on('click', '[data-lightbox-close]', (event) => {
    event.preventDefault();
    closeLightbox();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeLightbox();
      return;
    }

    /* Стрелки клавиатуры слушаем только при открытом окне: на самой странице
       они прокручивают её, и отнимать это ради галереи нельзя. */
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!document.querySelector('[data-lightbox]:not([hidden])')) return;
    event.preventDefault();
    stepLightbox(event.key === 'ArrowLeft' ? -1 : 1);
  });

  /* Листание пальцем — по самому кадру и по открытому окну. На телефоне ряд
     миниатюр уезжает под снимок, и до дальнего кадра надо сначала домотать
     ряд; палец по снимку там первое, что пробуют. */

  const SWIPE = 40;
  let swipe = null;

  document.addEventListener(
    'touchstart',
    (event) => {
      // Вращение модели и управление видео не должны перелистывать галерею.
      if (event.target instanceof Element && event.target.closest('[data-gallery-model], video, iframe')) {
        swipe = null;
        return;
      }
      const zone =
        event.touches.length === 1 && event.target instanceof Element
          ? event.target.closest('[data-gallery-frame], [data-lightbox]')
          : null;
      swipe = zone ? {zone, x: event.touches[0].clientX, y: event.touches[0].clientY, moved: false} : null;
    },
    {passive: true},
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      if (!swipe || event.touches.length !== 1) return;
      const dx = event.touches[0].clientX - swipe.x;
      const dy = event.touches[0].clientY - swipe.y;
      // Вбок — листание, вниз — прокрутка страницы. Решает то, чего больше.
      if (Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy)) swipe.moved = true;
    },
    {passive: true},
  );

  document.addEventListener('touchend', (event) => {
    const state = swipe;
    swipe = null;
    if (!state || !state.moved) return;

    /* Палец, ушедший вбок, не должен ещё и открывать крупный просмотр:
       браузер дорисовывает за касанием click, и отменяет его только
       preventDefault на touchend — потому слушатель и не passive. */
    event.preventDefault();

    const delta = event.changedTouches[0].clientX - state.x < 0 ? 1 : -1;
    if (state.zone.hasAttribute('data-lightbox')) stepLightbox(delta);
    else stepGallery(state.zone.closest('[data-gallery]'), delta);
  });

  /* ------------------------------------------------------------- вкладки

     Невыбранные панели остаются в разметке и прячутся атрибутом hidden,
     а не выбрасываются: так весь текст товара попадает в исходный HTML —
     поисковику и читалке экрана видно всё, а не только описание. */

  on('click', '[data-tab]', (event, button) => {
    const group = button.closest('[data-tabs]');
    if (!group) return;
    const name = button.dataset.tab;

    $$('[data-tab]', group).forEach((tab) => {
      const current = tab.dataset.tab === name;
      if (current) tab.setAttribute('aria-current', 'true');
      else tab.removeAttribute('aria-current');
      // Классы те же, что ставит Liquid при отрисовке: вкладки во второй
      // теме — пилюли с заливкой, а не подчёркнутые слова. Пока здесь
      // висел старый набор, содержимое переключалось, а чернела по-прежнему
      // первая вкладка: скрипт снимал и ставил классы, которых в разметке
      // нет вовсе.
      tab.classList.toggle('bg-night', current);
      tab.classList.toggle('text-paper', current);
      tab.classList.toggle('text-subtle', !current);
      tab.classList.toggle('hover:bg-surface', !current);
      tab.classList.toggle('hover:text-ink-strong', !current);
    });

    $$('[data-tab-panel]', group).forEach((panel) => {
      panel.hidden = panel.dataset.tabPanel !== name;
    });
  });

  /* ---------------------------------------------------- количество товара

     Shopify принимает только кратные шагу значения в границах правила
     и отклоняет остальные уже в корзине. Поэтому округляем здесь: покупателю
     честнее увидеть «10», чем получить отказ после нажатия «в корзину».

     Правило приходит с варианта (quantityRule): у розницы это всегда «от 1
     по 1», у оптовой локации — свой минимум, потолок и кратность.

     Потолок берём тот, что задан счётчику, без своей верхней границы:
     999 — разумный предел для штук, но у товара, который режут в размер,
     в этом поле метры, и забор в сто метров в 999 не помещается. Своё
     ограничение поверх заданного молча урезало бы заказ. */

  function clampToRule(value, picker) {
    const step = Math.max(1, Number(picker.dataset.step) || 1);
    const minimum = Math.max(step, Number(picker.dataset.min) || 1);
    const maximum = Number(picker.dataset.max) || 999;

    if (Number.isNaN(value)) return minimum;
    const snapped = Math.round(value / step) * step;
    return Math.min(maximum, Math.max(minimum, snapped));
  }

  /* Итог по строке считаем по цене того порога, который набран количеством:
     иначе на карточке одна сумма, а в корзине другая. */
  function unitPriceFor(quantity, picker) {
    const base = Number(picker.dataset.price) || 0;
    let breaks = [];
    try {
      breaks = JSON.parse(picker.dataset.breaks || '[]');
    } catch {
      breaks = [];
    }

    return breaks
      .filter((row) => quantity >= row.minimum)
      .reduce((best, row) => (row.price < best ? row.price : best), base);
  }

  function paintQuantity(picker) {
    const input = picker.querySelector('[data-quantity-input]');
    if (!input) return;

    const quantity = clampToRule(parseInt(input.value, 10), picker);
    input.value = String(quantity);

    const step = Math.max(1, Number(picker.dataset.step) || 1);
    const minimum = Math.max(step, Number(picker.dataset.min) || 1);
    const maximum = Number(picker.dataset.max) || 999;

    picker.querySelector('[data-quantity-step="-1"]')?.toggleAttribute('disabled', quantity <= minimum);
    picker.querySelector('[data-quantity-step="1"]')?.toggleAttribute('disabled', quantity >= maximum);

    const form = picker.closest('form');
    const hidden = form?.querySelector('input[name="quantity"]');
    if (hidden) hidden.value = String(quantity);

    const total = unitPriceFor(quantity, picker) * quantity;
    $$('[data-line-total]').forEach((node) => {
      node.textContent = formatMoney(total);
    });

    // Порог, который уже набран, подсвечивается в списке оптовых цен.
    $$('[data-break-minimum]').forEach((node) => {
      const reached = quantity >= Number(node.dataset.breakMinimum);
      node.classList.toggle('text-ink-strong', reached);
      node.classList.toggle('font-semibold', reached);
      node.classList.toggle('text-muted', !reached);
    });
  }

  on('click', '[data-quantity-step]', (event, button) => {
    event.preventDefault();
    const picker = button.closest('[data-quantity-picker]');
    const input = picker?.querySelector('[data-quantity-input]');
    if (!picker || !input) return;

    const step = Math.max(1, Number(picker.dataset.step) || 1);
    const delta = Number(button.dataset.quantityStep) * step;
    input.value = String(clampToRule((parseInt(input.value, 10) || 0) + delta, picker));
    paintQuantity(picker);
  });

  on('change', '[data-quantity-input]', (event, input) => {
    const picker = input.closest('[data-quantity-picker]');
    if (picker) paintQuantity(picker);
  });

  /* ------------------------------------------------------- липкая покупка

     Полоса с ценой и кнопкой появляется, когда блок покупки уехал за верхний
     край: до этого она повторяла бы то, что и так на экране. */

  let buyBarObserver = null;

  function watchBuyBar() {
    /* Наблюдателя всегда заводим заново: и полоса, и якорь приезжают с новой
       разметкой при смене варианта, а прежний остался бы смотреть на снятый
       узел — и копиться по одному на каждое нажатие. */
    buyBarObserver?.disconnect();

    const bar = $('[data-sticky-bar]');
    const anchor = $('[data-buy-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const gone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('translate-y-full', !gone);
        bar.classList.toggle('opacity-0', !gone);
        bar.setAttribute('aria-hidden', gone ? 'false' : 'true');
      },
      {rootMargin: '-120px 0px 0px 0px'},
    );

    observer.observe(anchor);
    buyBarObserver = observer;
  }

  /* ------------------------------------------------ переключение варианта

     Значения вариантов остаются настоящими ссылками — их можно скинуть,
     открыть в новой вкладке и проиндексировать. Но обычное нажатие
     перезагружало всю страницу: вместе с ценой и кадром заново приезжали
     отзывы, обвес, соседи и хлебные крошки, которые от варианта не зависят
     вовсе. Покупатель, перебирающий шесть покрытий, платил за это шестью
     полными загрузками и терял место, до которого доскроллил.

     Вместо этого просим у Shopify ту же секцию под новым адресом
     (Section Rendering API) и подменяем только те куски, которые вариант
     меняет. Разметку по-прежнему собирает сервер: ни цен, ни правил
     количества, ни таблицы совместимости вариантов на клиенте нет и не
     заводится — иначе витрина начала бы врать в тот день, когда владелец
     поменяет что-нибудь в админке.

     Отсюда и «части»: что зависит от варианта, помечено в Liquid атрибутом
     data-variant-part. Добавить новое место — значит поставить там атрибут,
     а не дописывать сюда список.

     Блок отзывов Judge.me нарочно не помечен ничем: это чужое приложение
     на Vue, оно живёт своей жизнью и от варианта не зависит. Подменить его
     разметку значило бы убить виджет — второй раз он не соберётся. */

  let variantToken = 0;
  let variantAbort = null;

  /* Поля, набранные руками. Оговорены поимённо, а не «любой input»: счётчик
     количества — тоже input, но его значение сервер отдаёт заново и правильно,
     а вот письмо в форме «сообщите о поступлении» взять больше неоткуда. */
  const TEXT_ENTRY =
    'textarea, input[type="text"], input[type="email"], input[type="tel"], input[type="search"], input:not([type])';

  /* Бережём только то, что человек набрал сам, и только пока он в этом поле.

     Раньше здесь стояло «фокус внутри куска — не трогаем», и это была ошибка
     с последствиями: нажатая мышью ссылка варианта сама лежит внутри блока
     покупки и забирает фокус, поэтому блок не подменялся никогда. Адрес,
     кадр и характеристики показывали новый вариант, а цена, выбранный цвет
     и скрытый id в форме оставались от прежнего — и в корзину уходил не тот
     товар, который покупатель видел на экране. */
  function holdsTypedText(node) {
    const focused = document.activeElement;
    return Boolean(
      focused &&
        node.contains(focused) &&
        focused.matches(TEXT_ENTRY) &&
        focused.value.trim() !== '',
    );
  }

  function swapVariantParts(fresh) {
    /* Нажатую ссылку заменит её двойник из новой разметки, и фокус улетел бы
       в начало страницы. Для клавиатуры это значит «перебирай каталог заново»,
       поэтому запоминаем адрес и возвращаем фокус на то же значение. */
    const focusedHref = document.activeElement
      ?.closest?.('[data-variant-link]')
      ?.getAttribute('href');

    $$('[data-variant-part]').forEach((node) => {
      const next = fresh.querySelector(`[data-variant-part="${node.dataset.variantPart}"]`);
      if (!next) return;
      if (holdsTypedText(node)) return;

      const replacement = document.importNode(next, true);
      // Keep the live model and its camera when choosing a different finish.
      const activeModel = node.dataset.variantPart === 'gallery'
        ? node.querySelector('[data-gallery-item]:not([hidden]) [data-gallery-model]')
        : null;
      const activeItem = activeModel?.closest('[data-gallery-item]');
      const nextItem = activeItem && replacement.querySelector(`[data-gallery-item="${activeItem.dataset.galleryItem}"]`);
      const nextModel = nextItem?.querySelector('[data-gallery-model]');
      const activeViewer = activeModel?.querySelector('model-viewer');
      if (nextModel && activeViewer?.getAttribute('src') === nextModel.querySelector('model-viewer')?.getAttribute('src')) {
        // Reconnecting model-viewer reapplies its attributes, so store the
        // user's actual camera there before moving it into the new section.
        if (activeViewer.getCameraOrbit) {
          const orbit = activeViewer.getCameraOrbit();
          const target = activeViewer.getCameraTarget();
          activeViewer.setAttribute('camera-orbit', `${orbit.theta}rad ${orbit.phi}rad ${orbit.radius}m`);
          activeViewer.setAttribute('camera-target', `${target.x}m ${target.y}m ${target.z}m`);
          activeViewer.setAttribute('field-of-view', `${activeViewer.getFieldOfView()}deg`);
        }
        activeModel.dataset.modelFinish = nextModel.dataset.modelFinish;
        nextModel.replaceWith(activeModel);
      }
      node.replaceWith(replacement);
      if (nextModel) showMedia(replacement.querySelector('[data-gallery]'), activeItem.dataset.galleryItem, false);
    });

    if (!focusedHref) return;
    const same = $$('[data-variant-link]').find(
      (link) => link.getAttribute('href') === focusedHref,
    );
    same?.focus({preventScroll: true});
  }

  async function switchVariant(href) {
    const root = $('[data-product-section]');
    if (!root) return false;

    const target = new URL(href, window.location.href);
    const request = new URL(target);
    request.searchParams.set('section_id', root.dataset.productSection);

    /* Перебор цветов идёт быстрее сети. Прошлый запрос отменяем, а ответы
       сверяем номером: пришедший невовремя нарисовал бы вариант, от которого
       уже отказались. */
    variantAbort?.abort();
    variantAbort = new AbortController();
    const token = ++variantToken;

    root.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(request, {signal: variantAbort.signal});
      if (!response.ok) throw new Error(`секция товара: ${response.status}`);

      const html = await response.text();
      if (token !== variantToken) return true;

      swapVariantParts(new DOMParser().parseFromString(html, 'text/html'));

      /* Адрес меняем через replaceState, а не pushState: перебор шести
         покрытий иначе оставил бы шесть записей в истории, и «назад» уводило
         бы на предыдущий цвет вместо каталога, откуда покупатель пришёл. */
      window.history.replaceState({}, '', target);
      repaint();
      return true;
    } catch (error) {
      // Отмена — это не сбой, а следующее нажатие: уходить по ссылке не нужно.
      return error.name === 'AbortError';
    } finally {
      if (token === variantToken) root.removeAttribute('aria-busy');
    }
  }

  on('click', '[data-variant-link]', (event, link) => {
    // Средняя кнопка, Ctrl и Shift — это работа браузера со ссылкой,
    // а не смена варианта на этой странице.
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();

    /* Сеть подвела или скрипт сломался — уходим по ссылке, как было бы вовсе
       без него. Нажатие уже перехвачено, и оставить его без последствий
       значит показать покупателю мёртвую кнопку. Отсюда и catch: одного
       then мало, отвергнутый промис прошёл бы мимо него молча. */
    const fallback = () => window.location.assign(link.href);

    switchVariant(link.href).then(
      (ok) => {
        if (!ok) fallback();
      },
      (error) => {
        console.error('вариант: не удалось подменить разметку', error);
        fallback();
      },
    );
  });

  /* ------------------------------------------------------------- фильтры

     Значения фильтров — обычные ссылки, страница перезагружается: набор живёт
     в адресе, им можно поделиться и его видит поисковик. На клиенте остаётся
     только панель поверх списка для узкого экрана и закрытие сортировки
     нажатием мимо. */

  on('click', '[data-filters-open]', (event) => {
    event.preventDefault();
    const panel = $('[data-filters-panel]');
    if (!panel) return;
    panel.hidden = false;
    document.body.classList.add('overlay-open');
  });

  on('click', '[data-filters-close]', (event) => {
    event.preventDefault();
    const panel = $('[data-filters-panel]');
    if (!panel) return;
    panel.hidden = true;
    document.body.classList.remove('overlay-open');
  });

  /* Колонку фильтров можно убрать: без неё в ряд встаёт четвёртая плитка,
     и на широком экране это заметно больше товара за один взгляд.

     Решение живёт на самой <aside>, и она нарочно не подменяется при смене
     фильтров — а вот полоса с кнопкой и сетка подменяются. Поэтому вид
     не переключаем, а перерисовываем от текущего состояния: после подмены
     та же функция вернёт убранной колонке убранный вид. */
  function paintFilterColumn() {
    const layout = $('[data-collection-layout]');
    const aside = $('[data-filters-aside]');
    if (!layout || !aside) return;

    // Сворачивать колонку умеет только тот раздел, где для этого есть кнопка.
    // Без неё функция не должна трогать классы вовсе: она бы навешивала
    // ширину колонки поверх той, что задана разметкой, и две ширины
    // расходились бы при каждой правке вёрстки.
    if (!$('[data-filters-toggle]')) return;

    const hidden = aside.hidden;
    layout.classList.toggle('lg:grid-cols-[17rem_1fr]', !hidden);
    layout.classList.toggle('lg:grid-cols-1', hidden);

    const grid = $('[data-product-grid]');
    if (grid) {
      grid.classList.toggle('lg:grid-cols-3', !hidden);
      grid.classList.toggle('md:grid-cols-3', hidden);
      grid.classList.toggle('xl:grid-cols-4', hidden);
    }

    const button = $('[data-filters-toggle]');
    if (button) {
      button.textContent = hidden ? button.dataset.showLabel : button.dataset.hideLabel;
    }
  }

  on('click', '[data-filters-toggle]', (event) => {
    event.preventDefault();
    const aside = $('[data-filters-aside]');
    if (!aside) return;
    aside.hidden = !aside.hidden;
    paintFilterColumn();
  });

  // Mobile and desktop have separate choices. The state survives replacement
  // of collection results by the Section Rendering API, including empty results.
  const GRID_VIEW_KEY = 'imsan:collection-grid:v1';
  const gridDesktop = window.matchMedia('(min-width: 1024px)');
  const gridWide = window.matchMedia('(min-width: 1280px)');
  let gridPreferences = {};
  try {
    const saved = JSON.parse(localStorage.getItem(GRID_VIEW_KEY) || '{}');
    if ([1, 2].includes(saved?.mobile)) gridPreferences.mobile = saved.mobile;
    if ([2, 3, 4].includes(saved?.desktop)) gridPreferences.desktop = saved.desktop;
  } catch (_) { /* Layout selection still works when storage is unavailable. */ }

  function paintCollectionView() {
    const grid = $('.collection-product-grid');
    const controls = $('[data-grid-controls]');
    if (!controls) return;
    controls.hidden = !grid;
    if (!grid) return;
    const desktop = gridDesktop.matches;
    const columns = desktop
      ? Math.min(gridPreferences.desktop || 3, gridWide.matches ? 4 : 3)
      : gridPreferences.mobile || 2;
    grid.style.setProperty('--collection-columns', columns);
    grid.dataset.gridColumns = columns;
    $$('[data-grid-choice]', controls).forEach((button) => {
      button.setAttribute('aria-pressed', String(Number(button.dataset.gridChoice) === columns));
    });
    // A single mobile card also needs a full-width image from the responsive set.
    $$('[data-card-image]', grid).forEach((image) => {
      image.sizes = `(min-width: 1024px) calc((min(100vw, 1320px) - 372px) / ${columns}), calc((100vw - 40px) / ${columns})`;
    });
  }

  on('click', '[data-grid-choice]', (_event, button) => {
    const columns = Number(button.dataset.gridChoice);
    const allowed = gridDesktop.matches ? (gridWide.matches ? [2, 3, 4] : [2, 3]) : [1, 2];
    if (!allowed.includes(columns)) return;
    gridPreferences[gridDesktop.matches ? 'desktop' : 'mobile'] = columns;
    try { localStorage.setItem(GRID_VIEW_KEY, JSON.stringify(gridPreferences)); } catch (_) { /* Keep the choice in memory. */ }
    paintCollectionView();
  });
  gridDesktop.addEventListener('change', paintCollectionView);
  gridWide.addEventListener('change', paintCollectionView);

  // Нажатие мимо закрывает список сортировки. Слушаем pointerdown, а не click:
  // список должен пропасть в момент нажатия, иначе он ещё мгновение висит
  // поверх того, по чему уже попали.
  document.addEventListener('pointerdown', (event) => {
    $$('[data-sort][open]').forEach((node) => {
      if (!node.contains(event.target)) node.removeAttribute('open');
    });
  });

  /* ------------------------------------------- фильтры без перезагрузки

     Тем же способом, что и варианты товара: просим у Shopify этот же раздел
     под новым адресом и подменяем помеченные куски — полосу над списком,
     колонку фильтров, сам список с листанием и панель на телефоне.

     Считает по-прежнему Shopify: и счётчики у значений, и число позиций,
     и то, какие значения вообще остались доступны после выбора. Считать это
     на клиенте значит держать вторую копию каталога в браузере.

     Помечены ссылки, а не «все ссылки внутри куска», и это важно: в списке
     лежат плитки товаров, и они обязаны уводить на товар, а не подменять
     раздел собой. */

  let filterToken = 0;
  let filterAbort = null;

  function swapFilterParts(fresh) {
    $$('[data-filter-part]').forEach((node) => {
      const next = fresh.querySelector(`[data-filter-part="${node.dataset.filterPart}"]`);
      if (!next) return;
      if (holdsTypedText(node)) return;
      node.replaceWith(document.importNode(next, true));
    });
  }

  async function applyFilters(href, options = {}) {
    const root = $('[data-collection-section]');
    if (!root) return false;

    const target = new URL(href, window.location.href);
    const request = new URL(target);
    request.searchParams.set('section_id', root.dataset.collectionSection);

    filterAbort?.abort();
    filterAbort = new AbortController();
    const token = ++filterToken;

    root.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(request, {signal: filterAbort.signal});
      if (!response.ok) throw new Error(`секция каталога: ${response.status}`);

      const html = await response.text();
      if (token !== filterToken) return true;

      swapFilterParts(new DOMParser().parseFromString(html, 'text/html'));

      /* Здесь pushState, а не replaceState, как у вариантов товара. Набор
         фильтров собирают по одному значению, и «назад» означает «сними
         последнее». У цвета товара ожидание обратное — там назад означает
         «вернись в каталог», и запись в истории на каждый цвет мешала бы.

         На возврате истории записи не добавляем: адрес уже сменил браузер. */
      if (options.history !== false) window.history.pushState({filters: true}, '', target);
      repaint();
      return true;
    } catch (error) {
      return error.name === 'AbortError';
    } finally {
      if (token === filterToken) root.removeAttribute('aria-busy');
    }
  }

  on('click', '[data-filter-link]', (event, link) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    const paging = link.dataset.filterLink === 'page';
    const fallback = () => window.location.assign(link.href);

    applyFilters(link.href).then(
      (ok) => {
        if (!ok) return fallback();
        /* Со страницы на страницу — к началу списка. Иначе «дальше» оставляет
           покупателя внизу, где новые товары уже над ним, и выглядит это как
           «кнопка не сработала». Выбор фильтра, наоборот, не двигаем: человек
           стоит у колонки и смотрит на неё. */
        if (paging) $('[data-filter-part="results"]')?.scrollIntoView({block: 'start'});
      },
      (error) => {
        console.error('фильтры: не удалось подменить разметку', error);
        fallback();
      },
    );
  });

  /* Цена — единственная форма среди фильтров. Со скриптами её отправляет
     ползунок (ниже), без них — кнопка из noscript. Адрес собираем из полей
     сами: браузер при method=get построил бы ровно такой же, только
     с перезагрузкой. */
  on('submit', '[data-filter-form]', (event, form) => {
    event.preventDefault();

    const url = new URL(form.getAttribute('action') || window.location.href, window.location.href);
    url.search = new URLSearchParams(new FormData(form)).toString();

    const fallback = () => form.submit();
    applyFilters(url.href).then(
      (ok) => {
        if (!ok) fallback();
      },
      (error) => {
        console.error('фильтры: не удалось подменить разметку', error);
        fallback();
      },
    );
  });

  /* ------------------------------------------ цена: линия с двумя точками

     Два input[type=range] лежат поверх одной линии, CSS снимает события мыши
     с полей и возвращает их кружкам — так ловятся обе точки. Здесь остаётся
     то, чего разметка не умеет: не давать точкам разойтись, красить отрезок
     между ними и применять выбранное, когда точку отпускают.

     Обработчики висят на документе: колонку фильтров подменяет Section
     Rendering API, и ссылки на снятые узлы протухают. */

  const priceParts = (box) => ({
    lo: $('[data-price-min]', box),
    hi: $('[data-price-max]', box),
    max: Number(box.dataset.max) || 0,
  });

  function paintPrice(box) {
    const {lo, hi, max} = priceParts(box);
    if (!lo || !hi || !max) return;

    /* Точки не меняются местами: та, которую тянут, упирается во вторую.
       Кто из них двигается, знает фокус — нажатие его уже перевело. */
    if (Number(lo.value) > Number(hi.value)) {
      if (document.activeElement === lo) lo.value = hi.value;
      else hi.value = lo.value;
    }

    const pct = (input) => (Number(input.value) / max) * 100;
    const selected = $('[data-price-selected]', box);
    if (selected) {
      selected.style.left = `${pct(lo)}%`;
      selected.style.right = `${100 - pct(hi)}%`;
    }

    const write = (selector, value) => {
      const node = $(selector, box);
      if (node) node.textContent = value;
    };
    write('[data-price-lo]', lo.value);
    write('[data-price-hi]', hi.value);

    /* Слой нижней точки поднимаем только во второй половине шкалы: сойдясь
       у левого края, тянуть надо верхнюю, у правого — нижнюю. */
    lo.style.zIndex = pct(lo) > 60 ? '2' : '';
  }

  let priceBox = null;
  let priceTimer = null;

  function commitPrice(box) {
    clearTimeout(priceTimer);
    priceTimer = null;

    const form = box.closest('form');
    const {lo, hi, max} = priceParts(box);
    if (!form || !lo || !hi) return;

    /* Края шкалы значат «без ограничения», и в адрес они не пишутся: иначе
       ссылка на весь раздел выглядела бы фильтром. Отключённое поле браузер
       не сериализует — этим и пользуемся, возвращая всё сразу обратно. */
    lo.disabled = Number(lo.value) <= 0;
    hi.disabled = Number(hi.value) >= max;
    const url = new URL(form.getAttribute('action') || window.location.href, window.location.href);
    url.search = new URLSearchParams(new FormData(form)).toString();
    lo.disabled = false;
    hi.disabled = false;

    /* Колонку подменят целиком, и фокус улетит в начало страницы — для
       клавиатуры это «выбирай заново». Возвращаем его на ту же точку. */
    let focused = null;
    if (document.activeElement === lo) focused = '[data-price-min]';
    if (document.activeElement === hi) focused = '[data-price-max]';

    const fallback = () => form.submit();
    applyFilters(url.href).then(
      (ok) => {
        if (!ok) return fallback();
        if (focused) $(focused)?.focus();
      },
      (error) => {
        console.error('фильтры: не удалось применить цену', error);
        fallback();
      },
    );
  }

  on('input', '[data-price-range] input[type="range"]', (event, input) => {
    const box = input.closest('[data-price-range]');
    if (!box) return;
    paintPrice(box);
    // Мышью применяем по отпусканию, стрелками — когда перестали нажимать.
    if (priceBox) return;
    clearTimeout(priceTimer);
    priceTimer = setTimeout(() => commitPrice(box), 500);
  });

  on('pointerdown', '[data-price-range] input[type="range"]', (event, input) => {
    priceBox = input.closest('[data-price-range]');
    clearTimeout(priceTimer);
  });

  /* Нажатие по самой линии: двигаем ближнюю точку туда. Кружки события мыши
     перехватывают сами, поэтому сюда попадают только нажатия мимо них — но
     проверить всё равно надо: событие с кружка браузер приписывает полю,
     а поле лежит внутри линии. */
  on('pointerdown', '[data-price-track]', (event, track) => {
    if (event.target instanceof Element && event.target.closest('input[type="range"]')) return;

    const box = track.closest('[data-price-range]');
    if (!box) return;
    const {lo, hi, max} = priceParts(box);
    if (!lo || !hi || !max) return;

    const rect = track.getBoundingClientRect();
    if (!rect.width) return;
    const share = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const value = Math.round(share * max);

    const target = Math.abs(value - Number(lo.value)) <= Math.abs(value - Number(hi.value)) ? lo : hi;
    target.value = value;
    target.focus();
    paintPrice(box);
    commitPrice(box);
  });

  // Отпустить точку можно и мимо неё — палец уезжает за край колонки.
  document.addEventListener('pointerup', () => {
    if (!priceBox) return;
    const box = priceBox;
    priceBox = null;
    commitPrice(box);
  });

  /* «Назад» на собранном наборе. Адрес браузер уже сменил — остаётся привести
     к нему разметку. На страницах без раздела не вмешиваемся: там возврат
     истории обрабатывает сам браузер. */
  window.addEventListener('popstate', () => {
    if (!$('[data-collection-section]')) return;
    applyFilters(window.location.href, {history: false});
  });

  /* ------------------------------------------------- калькулятор дверей

     Геометрия перенесена из старого калькулятора один в один: зазоры оснастки
     здесь — свойства системы, а не проёма, поэтому они константы. Цены
     комплектующих приходят разметкой из карточек товаров, поэтому смета
     не расходится с каталогом.

     НДС отдельно не начисляется: цены магазина уже с налогом, иначе он
     посчитался бы дважды. */

  const RAIL_TOP_GAP = 44;
  const RAIL_BOTTOM_GAP = 10.6;
  const GASKET_BASE = 1;
  /* Щётка съедает по 4 мм с каждой стороны наполнения. */
  const BRUSH_INSET = 4;
  /* Запас на подрезку хлыста: из пятиметрового профиля целые куски не выходят. */
  const TRIM_ALLOWANCE = 1.1;

  const roundTo = (value, precision = 2) => {
    const factor = 10 ** precision;
    return Math.round((Number.isFinite(value) ? value : 0) * factor) / factor;
  };

  const clampNumber = (value, min, max) => {
    if (!Number.isFinite(value)) return min;
    return Math.min(max, Math.max(min, value));
  };

  /* Границы расчёта. Держим их здесь, а не только в атрибутах поля: max
     у number браузер применяет к стрелкам и к проверке формы, а напечатать
     мимо него даёт. Расчёт границы соблюдал всегда, поле — нет, и на «111
     дверей» смета молча считала восемь: 227,5 мм полотно, 3,993 м² и 743 €
     под числом 111. Одно место на оба применения, чтобы не разошлись. */
  const DOOR_LIMITS = {
    openingWidth: [400, 8000],
    openingHeight: [400, 5000],
    doorCount: [1, 8],
  };

  /**
   * Возвращает в поле то число, по которому считали.
   *
   * Нижнюю границу трогаем только когда поле покидают: «2500» начинается
   * с «2», и зажатое на лету оно превратилось бы в 400 на первом же нажатии.
   * Верхнюю — сразу: набрать больше максимума всё равно нечем, и пусть
   * покупатель видит предел там же, где его встретил.
   */
  const doorLimits = (name) => DOOR_LIMITS[name];

  function clampDoorField(field, withMin) {
    const limits = doorLimits(field.dataset.doorsField);
    if (!limits || field.value === '') return;
    const value = Number(field.value);
    if (!Number.isFinite(value)) return;
    const fixed = Math.round(withMin ? clampNumber(value, limits[0], limits[1]) : Math.min(value, limits[1]));
    if (String(fixed) !== field.value) field.value = String(fixed);
  }

  /* ------------------------------------------------------- конструктор

     Дверь — набор секций, а не один лист. Секции идут строками (разделитель
     поперёк полотна) или столбцами (разделитель повёрнут на 90°). Высоту
     задают всем, кроме последней: последняя забирает остаток, и дверь всегда
     сходится сама.

     План живёт здесь, а не в разметке: строк секций столько, сколько их
     завели, и держать это состояние в атрибутах значило бы читать его
     обратно из разметки на каждый пересчёт. */

  let doorPlan = null;
  /* Какую секцию сейчас правит покупатель: выбор материала относится к ней. */
  let activeDoor = 0;
  let activeSection = 0;

  const newSection = (fill) => ({fill, size: 0});

  function makeDoor(fill) {
    /* Доводчик — один на створку и хранится вместе с ней: он и в смету
       идёт по двери, и движение в предпросмотре меняет у своей створки.
       Пустая строка значит «без доводчика». */
    return {split: 'rows', closer: '', sections: [newSection(fill)]};
  }

  /** Подгоняет план под число дверей и правило «все одинаковые». */
  function ensurePlan(input, config) {
    const fallback = config.materials[0] ? config.materials[0].handle : '';
    if (!doorPlan) doorPlan = [makeDoor(fallback)];

    while (doorPlan.length < input.doorCount) {
      doorPlan.push(cloneDoor(doorPlan[doorPlan.length - 1]));
    }
    if (doorPlan.length > input.doorCount) doorPlan.length = input.doorCount;

    if (input.sameDoors) {
      /* Правят первую дверь, остальные повторяют её. Ту, что правят,
         не трогаем: иначе редактирование третьей двери в режиме «все
         одинаковые» отменяло бы само себя. */
      const source = doorPlan[Math.min(activeDoor, doorPlan.length - 1)];
      doorPlan = doorPlan.map(() => cloneDoor(source));
    }

    /* Доводчик, переставший подходить створке, снимаем. Число дверей меняют
       уже после выбора: центральный, поставленный на трёх, на четырёх повис
       бы на двери, у которой середины больше нет.

       Пока двери одинаковые, доводчик у них общий — и снимается он у всех
       разом, иначе «одинаковые» перестанут быть одинаковыми. */
    const suits = (handle, at) =>
      closerFits(placementOf(handle, config), at, doorPlan.length);
    if (input.sameDoors) {
      const shared = doorPlan[0].closer;
      if (shared && !doorPlan.every((_, at) => suits(shared, at))) {
        for (const door of doorPlan) door.closer = '';
      }
    } else {
      doorPlan.forEach((door, at) => {
        if (door.closer && !suits(door.closer, at)) door.closer = '';
      });
    }

    activeDoor = Math.min(activeDoor, doorPlan.length - 1);
    activeSection = Math.min(activeSection, doorPlan[activeDoor].sections.length - 1);
    return doorPlan;
  }

  const cloneDoor = (door) => ({
    split: door.split,
    closer: door.closer || '',
    sections: door.sections.map((s) => ({fill: s.fill, size: s.size})),
  });

  /**
   * Размеры секций по длине полотна.
   *
   * Секции всегда занимают наполнение целиком: ни лишнего, ни свободного.
   * Дверь — это кусок дерева заданной длины, и «не помещается» у неё не
   * бывает: увеличили одну секцию — соседняя ужалась, вот и весь разговор.
   * Раньше сумма могла разойтись с наполнением, и об этом сообщала строка
   * с ошибкой — сообщать было нечего, чинить надо было размеры.
   *
   * Это места секций, а не размеры деталей: разделитель садится на шов
   * между ними и забирает своё из обеих сразу, поэтому места складываются
   * в наполнение целиком, а вырезают деталь меньше — см. dividerInset.
   *
   * Записанное меняем только когда наполнение поехало само — сменили
   * высоту проёма, профиль, доводчик. Тогда секции тянутся вместе с ним
   * и сохраняют пропорции: заданные покупателем доли важнее чисел.
   */
  function sectionSizes(door, available) {
    const count = door.sections.length;
    const usable = Math.max(0, available);
    const even = () => door.sections.map(() => usable / count);

    if (usable < MIN_SECTION * count) return {sizes: even(), usable};

    let sizes = door.sections.map((s) => Math.max(0, Number(s.size) || 0));
    const total = sizes.reduce((sum, value) => sum + value, 0);
    if (total <= 0) sizes = even();
    else if (Math.abs(total - usable) >= 0.05) sizes = sizes.map((v) => (v * usable) / total);

    /* После растяжки секция может опуститься ниже минимума — тогда делим
       поровну: подпирать одну за счёт другой значит менять пропорции,
       а они после такого сжатия уже ничего не значат. */
    if (sizes.some((v) => v < MIN_SECTION)) sizes = even();

    /* Показываем целые миллиметры, поэтому дробь наполнения отдаём
       последней: иначе числа в списке не складывались бы в наполнение
       над ним, и покупатель считал бы разницу вручную. */
    const head = sizes.slice(0, -1).map((v) => Math.round(v));
    const tail = usable - head.reduce((sum, v) => sum + v, 0);
    return {sizes: [...head, tail], usable};
  }

  /**
   * Правка высоты секции: место забирается у соседей, а не берётся из
   * воздуха.
   *
   * Отдаём соседу сверху, у него же и отнимаем — правят обычно сверху вниз,
   * и первой на глазах меняется та секция, которую только что прошли.
   * У самой верхней соседей сверху нет, она разбирается с нижней.
   * Если у ближнего соседа места не хватило, идём дальше через одного.
   *
   * Больше, чем есть, вписать нельзя: соседям остаётся хотя бы по минимуму,
   * и поле возвращает то число, которое поместилось.
   */
  function resizeSection(door, at, wanted, usable) {
    const count = door.sections.length;
    const sizes = sectionSizes(door, usable).sizes;
    if (count < 2) return;

    const value = clampNumber(wanted, MIN_SECTION, usable - MIN_SECTION * (count - 1));
    let debt = value - sizes[at];
    sizes[at] = value;

    const order = [];
    for (let i = at - 1; i >= 0; i -= 1) order.push(i);
    for (let i = at + 1; i < count; i += 1) order.push(i);

    for (const i of order) {
      if (Math.abs(debt) < 0.001) break;
      // Отнять можно только излишек над минимумом, отдать — сколько угодно.
      const give = debt > 0 ? Math.min(debt, sizes[i] - MIN_SECTION) : debt;
      sizes[i] -= give;
      debt -= give;
    }

    door.sections.forEach((section, i) => {
      section.size = sizes[i];
    });
  }

  const MIN_SECTION = 40;
  const MAX_SECTIONS = 8;

  /**
   * Сколько разделитель забирает у секции с одной стороны.
   *
   * Разделитель стоит на шве и делит себя между соседями поровну: по
   * половине ширины в каждую. Стеклу и зеркалу добавляется миллиметр под
   * резинку — та же, что идёт по краю полотна, у шва она никуда не девается.
   *
   * При семи миллиметрах это привычные мастерской 3,5 на плиту и 4,5 на
   * стекло; числа не вписаны руками, чтобы другой разделитель не разошёлся
   * с раскроем молча.
   */
  function dividerInset(fill, dividerWidth) {
    return dividerWidth / 2 + (fill.kind === 'glass' ? GASKET_BASE : 0);
  }

  /**
   * Можно ли ставить такой доводчик на такую створку.
   *
   * Крайний тормозит створку об упор, а упор стоит по краям проёма.
   * Центральный сводит створки к середине, и середина в комплекте бывает
   * одна — в трёх дверях. Остальные идут куда угодно.
   */
  function placementOf(handle, config) {
    const closer = (config.closers || []).find((row) => row.handle === handle);
    return (closer && closer.placement) || 'any';
  }

  function closerFits(placement, index, count) {
    if (placement === 'side') return index === 0 || index === count - 1;
    if (placement === 'centre') return count === 3 && index === 1;
    return true;
  }

  /** Наполнение секции: стекло из списка или плита с приставкой plate:. */
  function fillOf(handle, config) {
    if (String(handle).startsWith(PLATE_CHOICE + ':')) {
      const wanted = String(handle).slice(PLATE_CHOICE.length + 1);
      const plate = (config.plates || []).find((row) => row.handle === wanted);
      if (plate) return {kind: 'plate', handle, label: plate.label, plate, image: plate.image};
    }
    if (String(handle).startsWith(CUSTOM_CHOICE + ':') || handle === CUSTOM_CHOICE) {
      const custom = customFill(handle) || {incomplete: true};
      return {
        kind: 'glass',
        handle,
        label: custom.note
          ? `${doorsText('customLabel')} · ${custom.note}`
          : doorsText('customLabel'),
        pricePerM2: custom.rate || 0,
        incomplete: Boolean(custom.incomplete),
        image: '',
        tint: '',
      };
    }

    const material =
      config.materials.find((row) => row.handle === handle) || config.materials[0];
    if (!material) return null;
    return {
      kind: 'glass',
      handle: material.handle,
      label: material.label,
      pricePerM2: material.pricePerM2,
      image: material.image || '',
      // Пустая краска значит зеркало: его рисует отражение, а не заливка.
      tint: material.tint || '',
    };
  }

  /* --------------------------------------------------------- раскрой плиты

     Плиту поставщик режет пополам и ещё раз пополам, поэтому купить можно
     четверть, половину, три четверти и дальше с шагом в четверть. Половин
     две — вдоль длинной стороны и вдоль короткой, — и это не формальность:
     деталь 2436 мм влезает только в ту, где длинная сторона осталась целой.

     Считать долю по площади нельзя. Из листа 2800×2070 два полотна
     890×2436 выходят с запасом, а из двух его половин — только по одному:
     рез посередине съедает третье полотно, которого «по площади хватало».

     С секциями деталей стало много и разных, поэтому лист набивается так,
     как его режут на самом деле: сперва распускают на полосы по ширине
     детали, потом полосу режут поперёк. Детали одной ширины делят полосу,
     разной — занимают свои. */

  const SAW_KERF = 4;
  const PLATE_TRIM = 10;
  const PLATE_CHOICE = 'plate';
  const CUSTOM_CHOICE = 'custom';

  /* Поля, которые правят наполнение секции: сам выбор, декор плиты и числа
     своего наполнения. Одним набором, чтобы ввод и потеря фокуса слушали
     одно и то же. */
  const CUSTOM_FIELDS = new Set([
    'materialHandle',
    'plateHandle',
    'customSquarePrice',
  ]);

  /* Строки-шаблоны калькулятора лежат на его корне: см. main-doors-calculator.
     Читаем их по месту, а не тащим через config: config собирается из
     метаобъектов, а это словарь витрины. */
  const doorsText = (key) => $('[data-doors]')?.dataset[key] || '';

  /**
   * Своё наполнение: покупатель называет либо лист с ценой, либо цену
   * за квадрат. В строке секции это едет как
   * «custom:способ:ширина:высота:цена листа:цена за м²» — тем же приёмом,
   * что и плита, чтобы состояние секции осталось одной строкой.
   *
   * Хранятся оба набора чисел, а не только те, что считают сейчас:
   * переключение способа не должно стирать набранное, а секций несколько,
   * и у каждой свои числа.
   *
   * Незаполненное поле — не ноль: считать наполнение бесплатным нельзя,
   * поэтому такой раскрой помечается неполным и смета блокируется.
   */
  function customParts(handle) {
    return {squarePrice: String(handle).split(':')[1] || ''};
  }

  function customFill(handle) {
    if (String(handle).split(':')[0] !== CUSTOM_CHOICE) return null;
    const rate = Number(customParts(handle).squarePrice);
    if (!(rate > 0)) return {incomplete: true};
    return {rate, note: doorsText('customRate').replace('{p}', formatRate(rate))};
  }

  /** Цена в строке: без хвостовых нулей, но и без длинной дроби. */
  const formatRate = (value) =>
    (Math.round(value * 100) / 100).toFixed(2).replace(/[.,]?0+$/, '').replace('.', ',');

  /**
   * Влезает ли набор деталей в кусок rw×rh.
   * rw — вдоль текстуры: туда ложится высота детали.
   */
  function packRegion(rw, rh, panels, kerf) {
    const columns = [];
    for (const panel of panels) {
      if (panel.h > rw || panel.w > rh) return false;
      let placed = false;
      for (const column of columns) {
        if (column.w !== panel.w) continue;
        if (column.used + kerf + panel.h > rw) continue;
        column.used += kerf + panel.h;
        placed = true;
        break;
      }
      if (!placed) columns.push({w: panel.w, used: panel.h});
    }
    const across = columns.reduce((sum, c) => sum + c.w, 0) + kerf * (columns.length - 1);
    return across <= rh;
  }

  /** Сколько деталей из списка влезет в кусок: берём по одной, пока лезут. */
  function greedyFit(rw, rh, panels, kerf) {
    const taken = [];
    for (const panel of panels) {
      taken.push(panel);
      if (!packRegion(rw, rh, taken, kerf)) taken.pop();
    }
    return taken;
  }

  /**
   * Сколько плит взять под набор деталей.
   *
   * Сперва жадно набиваем целые листы, а остаток покупаем наименьшей долей,
   * в которую он умещается. Цена у поставщика строго пропорциональна доле,
   * поэтому меньше четвертей — дешевле, и искать нужно именно их.
   */
  function platePlan(plate, panels, settings) {
    const kerf = settings.kerf > 0 ? settings.kerf : SAW_KERF;
    const trim = settings.trim >= 0 ? settings.trim : PLATE_TRIM;
    const W = plate.width;
    const H = plate.height;

    /* Текстура идёт вдоль длинной стороны, и в двери она обязана стоять
       вертикально: высота детали ложится вдоль длины листа, повернуть
       нельзя. У однотонной плиты владелец снимает галочку — тогда
       пробуем и поперёк, и берём то, что вышло дешевле. */
    const orientations = plate.grain === false
      ? [panels, panels.map((p) => ({w: p.h, h: p.w, area: p.area}))]
      : [panels];

    let best = null;
    for (const set of orientations) {
      const plan = packPlates(W, H, set, kerf, trim, plate.buyStep);
      if (plan && (!best || plan.quarters < best.quarters)) best = plan;
    }
    if (!best) return {fits: false, plates: 0, perPlate: 0, waste: 0};

    const boughtArea = (best.quarters / 4) * W * H;
    const usedArea = panels.reduce((sum, p) => sum + p.w * p.h, 0);
    return {
      fits: true,
      plates: best.quarters / 4,
      perPlate: best.perPlate,
      waste: roundTo(1 - usedArea / boughtArea, 3),
    };
  }

  function packPlates(W, H, panels, kerf, trim, buyStep) {
    const full = [W - 2 * trim, H - 2 * trim];
    // Доли: четверть, половина вдоль длинной стороны, вдоль короткой, три четверти.
    const region = (quarters) => {
      if (quarters >= 4) return [full];
      if (quarters === 1) return [[W / 2 - trim, H / 2 - trim]];
      if (quarters === 2) return [[W - 2 * trim, H / 2 - trim], [W / 2 - trim, H - 2 * trim]];
      // Три четверти — буква Г; раскладывается на половину и четверть двумя
      // способами, обе проверяем через набивку по кускам.
      return [[W - 2 * trim, H / 2 - trim], [W / 2 - trim, H - 2 * trim]];
    };

    const sorted = [...panels].sort((a, b) => b.h - a.h || b.w - a.w);
    if (!sorted.length) return {quarters: 0, perPlate: 0};
    if (!packRegion(full[0], full[1], [sorted[0]], kerf)) return null;

    const perPlate = greedyFit(full[0], full[1], sorted, kerf).length;

    let rest = sorted;
    let sheets = 0;
    while (rest.length) {
      const take = greedyFit(full[0], full[1], rest, kerf);
      if (!take.length) return null;
      if (take.length === rest.length) break;
      sheets += 1;
      rest = rest.filter((panel) => !take.includes(panel));
    }

    const step = buyStep === 0.25 ? 1 : 4;
    for (let quarters = step; quarters <= 4; quarters += step) {
      const fits = region(quarters).some(([rw, rh]) => {
        if (quarters !== 3) return packRegion(rw, rh, rest, kerf);
        // Г-образный кусок: половина плюс четверть, добираем остаток.
        const inHalf = greedyFit(rw, rh, rest, kerf);
        const left = rest.filter((panel) => !inHalf.includes(panel));
        return packRegion(W / 2 - trim, H / 2 - trim, left, kerf);
      });
      if (fits) return {quarters: sheets * 4 + quarters, perPlate};
    }
    return {quarters: (sheets + 1) * 4, perPlate};
  }

  /** «1½», «¾», «2» — доля плиты человеческой записью. */
  function plateFraction(value) {
    const whole = Math.floor(value + 1e-9);
    const rest = Math.round((value - whole) * 4);
    const marks = ['', '¼', '½', '¾'];
    if (!rest) return String(whole);
    return whole ? `${whole}${marks[rest]}` : marks[rest];
  }

  function doorGeometry(input, profile) {
    /* Уплотнитель стоит в каждой двери, поэтому и паз под него в геометрии
       безусловный: выбором он был, пока его можно было не брать. */
    const brush = BRUSH_INSET;
    // Доводчик поднимает дверь на 4 мм — ручка становится короче.
    const handleDrop = input.doors.some((door) => door.closer) ? 44 : 40;

    const overlapStep = profile.topInset + 1 + brush;
    const fillingInset = profile.bottomInset + brush;
    const connectorInset = profile.topInset + brush;

    const doorWidth = (input.openingWidth + input.overlapCount * overlapStep) / input.doorCount;
    const handleLength = input.openingHeight - handleDrop;

    const fillingHeight10 = handleLength - (RAIL_TOP_GAP + RAIL_BOTTOM_GAP);
    const fillingWidth10 = doorWidth - fillingInset * 2;
    const fillingHeight4 = fillingHeight10 - GASKET_BASE * 2;
    const fillingWidth4 = fillingWidth10 - GASKET_BASE * 2;

    return {
      doorWidth: roundTo(doorWidth, 1),
      handleLength: roundTo(handleLength, 1),
      connectorLength: roundTo(doorWidth - connectorInset * 2, 1),
      fillingWidth4: roundTo(fillingWidth4, 1),
      fillingHeight4: roundTo(fillingHeight4, 1),
      fillingWidth10: roundTo(fillingWidth10, 1),
      fillingHeight10: roundTo(fillingHeight10, 1),
      overlapStep: roundTo(overlapStep, 1),
      fillingInset: roundTo(fillingInset, 1),
    };
  }

  /**
   * Разбирает двери на детали: что резать, из чего и какого размера.
   *
   * Секции идут строками или столбцами, поэтому у детали меняется не только
   * размер, но и то, что делит полотно: у строк разделитель длиной с
   * перемычку, у столбцов — во всю высоту наполнения.
   */
  function doorPanels(input, geometry, config, dividerWidth) {
    const panels = [];
    let dividerLength = 0;
    let dividers = 0;

    input.doors.forEach((door, index) => {
      const rows = door.split === 'rows';
      const along = rows ? geometry.fillingHeight10 : geometry.fillingWidth10;
      const across = rows ? geometry.fillingWidth10 : geometry.fillingHeight10;
      const layout = sectionSizes(door, along);

      dividers += door.sections.length - 1;
      dividerLength += (door.sections.length - 1) * across;

      door.sections.forEach((section, at) => {
        const fill = fillOf(section.fill, config);
        if (!fill) return;
        /* Разделителей у секции столько, сколько у неё соседей: у крайней
           один, у средней два, у единственной ни одного. */
        const touching = (at > 0 ? 1 : 0) + (at < door.sections.length - 1 ? 1 : 0);
        const size = Math.max(0, layout.sizes[at] - dividerInset(fill, dividerWidth) * touching);
        panels.push({
          door: index,
          section: at,
          fill,
          w: roundTo(rows ? across : size, 1),
          h: roundTo(rows ? size : across, 1),
          area: (across * size) / 1_000_000,
        });
      });
    });

    return {panels, dividers, dividerLength};
  }

  function doorQuote(input, config) {
    const profile = config.profiles.find((row) => row.handle === input.profileHandle) || config.profiles[0];
    if (!profile || !config.materials.length) return null;

    const geometry = doorGeometry(input, profile);
    const parts = config.parts;
    const dividerWidth = Number(config.dividerWidthMm) > 0 ? Number(config.dividerWidthMm) : 7;
    const cut = doorPanels(input, geometry, config, dividerWidth);

    const glass = cut.panels.filter((p) => p.fill.kind === 'glass');
    const glassArea = glass.reduce((sum, p) => sum + p.area, 0);
    const materialArea = cut.panels.reduce((sum, p) => sum + p.area, 0);

    /* Плиты считаем по декорам: у каждого свой лист, и делить его между
       разными декорами не с чем. */
    const byPlate = new Map();
    for (const panel of cut.panels) {
      if (panel.fill.kind !== 'plate') continue;
      const key = panel.fill.plate.handle;
      if (!byPlate.has(key)) byPlate.set(key, {plate: panel.fill.plate, panels: []});
      byPlate.get(key).panels.push(panel);
    }

    const plateLines = [];
    let plateCost = 0;
    /* Незаполненное своё наполнение блокирует смету так же, как не влезшая
       плита: цены у него нет, и складывать нечего. */
    let blocked = cut.panels.some((panel) => panel.fill.incomplete);
    for (const {plate, panels} of byPlate.values()) {
      const plan = platePlan(plate, panels, {kerf: config.sawKerfMm, trim: config.plateTrimMm});
      if (!plan.fits) blocked = true;
      else plateCost += plan.plates * plate.price;
      plateLines.push({plate, plan, pieces: panels.length});
    }

    /* Профиль продаётся хлыстом, поэтому всё считаем долями хлыста. */
    const stickLength = Math.max(0.1, config.profileLengthM) * 1000;
    const sticks = (mm) => (mm / stickLength) * TRIM_ALLOWANCE;

    const trackQuantity = sticks(input.openingWidth);
    const frameQuantity = sticks(geometry.connectorLength * input.doorCount);
    const handleQuantity = sticks(2 * geometry.handleLength * input.doorCount);
    const dividerQuantity = sticks(cut.dividerLength);

    const brushQuantity =
      (1 / 250) * (geometry.handleLength / 1000) * input.doorCount * 2 * TRIM_ALLOWANCE;

    /* Уплотнитель 10/4 добирает паз до толщины стекла — по периметру каждой
       стеклянной секции. Плита входит в паз как есть, ей он не нужен, как
       и плёнка, которой оклеивают зеркало.

       Считаем долями той бухты, которую покупаем: цена приходит с карточки
       товара, и делить её на заводскую стометровую бухту, когда у нас
       намотано двести пятьдесят, значит завысить строку в два с половиной
       раза. Длина бухты — поле настройки. */
    const gasketRoll = Math.max(1, Number(config.gasketRollM) || 100) * 1000;
    const gasketQuantity =
      glass.reduce((sum, p) => sum + ((p.w + p.h) * 2) / gasketRoll, 0) * 1.05;

    /* Плёнка — так же: делим не на заводские 80 м², а на площадь нашего
       рулона. У «200 мм × 80 м» это шестнадцать квадратов. */
    const filmRoll = Math.max(1, Number(config.filmRollM2) || 80);

    const line = (unitPrice, quantity) => {
      if (quantity <= 0 || !unitPrice) return 0;
      return quantity * unitPrice;
    };

    /* По одному доводчику на створку: исполнений девять, у каждого своя
       цена, а на дверь ставят один. Дверь без доводчика в эту сумму
       не попадает вовсе. */
    const closerCost = input.doors.reduce((sum, door) => {
      const closer = (config.closers || []).find((item) => item.handle === door.closer);
      return closer ? sum + closer.price : sum;
    }, 0);

    /* Наценки здесь нет нарочно. В записи наполнения лежит конечная цена
       за квадрат с НДС — такая же, как цена шины или ролика в карточке
       товара. Раньше поверх неё лежал ещё множитель, задуманный для
       закупочной цены, и квадрат зеркала уходил с 35 € на 98 €. */
    const glassCost = glass.reduce((sum, p) => sum + p.area * p.fill.pricePerM2, 0);

    const total =
      line(profile.tracks.bottomDouble, trackQuantity) +
      line(profile.tracks.topDouble, trackQuantity) +
      line(profile.tracks.bottomSupport, frameQuantity) +
      line(profile.tracks.topSupport, frameQuantity) +
      line(profile.tracks.divider, dividerQuantity) +
      line(profile.handlePrice, handleQuantity) +
      line(parts.rollerKit, input.doorCount) +
      line(parts.stopper, input.doorCount * 2) +
      line(parts.clip, input.doorCount * 4) +
      line(parts.bufferSeal, brushQuantity) +
      line(parts.gasket, gasketQuantity) +
      line(parts.safetyFilm, (glassArea / filmRoll) * 1.3) +
      closerCost +
      line(parts.packageFilm, input.doorCount / 10) +
      line(parts.cardboard, input.doorCount * 4) +
      line(config.labourPerDoor, input.doorCount) +
      glassCost +
      plateCost;

    return {
      geometry,
      profile,
      cut,
      plateLines,
      dividerWidth,
      /* Цена щётки — сколько она будет стоить, а не сколько стоит сейчас:
         рядом с галочкой ноль ничего не сообщает, а число помогает решить. */
      brushCost: roundTo(
        line(
          parts.bufferSeal,
          (1 / 250) * (geometry.handleLength / 1000) * input.doorCount * 2 * TRIM_ALLOWANCE,
        ),
      ),
      closerCost: roundTo(closerCost),
      materialArea: roundTo(materialArea, 3),
      blocked,
      total: roundTo(total),
    };
  }

  /* ------------------------------------------------------- предпросмотр

     Рисуем в миллиметрах: viewBox — сам проём, и ничего пересчитывать
     в пиксели не нужно. Каждая створка — своя группа с номером, и она же
     ездит: открывание сделано сдвигом группы, а не перерисовкой.

     Плита показывается своим снимком, стекло — цветом с бликом. Пустой цвет
     значит зеркало: у зеркала своего цвета нет, оно берёт его у комнаты,
     поэтому рисуется отражением, а не заливкой. */

  /** Какие створки сейчас отъехали. Переживает перерисовку картинки. */
  const doorOpen = new Set();

  /* Высота картинки: то же число, что стоит в разметке у рамки (max-h-[26rem]). */
  const PREVIEW_HEIGHT_REM = 26;

  /* Полоса, выбранная покупателем, по номеру створки. Пусто — как встанет
     само. Живёт отдельно от плана дверей: пока двери одинаковые, план у них
     общий, а полоса общей быть не может — на одной им не разъехаться. */
  const railChoice = new Map();

  /**
   * На какой полосе стоит створка: 0 — передняя, 1 — задняя.
   *
   * Само собой створки раскладываются от середины наружу: центральная
   * выходит вперёд, следующая уходит назад, и так до краёв. Так средние
   * двери и стоят в шкафу — их открывают чаще всего, и лезть за соседнюю
   * при этом не приходится.
   *
   * Разъехаться могут только соседи по разным полосам: на одной они
   * упираются друг в друга. У чётного числа дверей середина приходится
   * между двумя створками — обе выходят вперёд, сходятся посередине
   * и открываются наружу, каждая над своей крайней.
   *
   * Две двери симметрией не разложить: обе центральные, и на одной полосе
   * им некуда деться. Их оставляем как было — первая впереди.
   */
  function defaultRail(index, count) {
    if (count < 3) return index === 0 ? 0 : 1;
    return Math.floor(Math.abs(index - (count - 1) / 2)) % 2;
  }

  function doorRail(index, count) {
    const picked = railChoice.get(index);
    return picked === 0 || picked === 1 ? picked : defaultRail(index, count);
  }

  /**
   * Стыки, на которых створки заходят друг на друга.
   *
   * Заходят только соседи по разным полосам: на одной они сходятся встык,
   * иначе им не разъехаться. Отсюда и потолок нахлёстов — их столько,
   * сколько таких стыков, и на четырёх дверях с центральными впереди
   * это два, а не три.
   */
  function railSeams(count) {
    const seams = [];
    for (let at = 1; at < count; at += 1) {
      if (doorRail(at, count) !== doorRail(at - 1, count)) seams.push(at);
    }
    return seams;
  }

  const svgEl = (name, attrs) => {
    const node = document.createElementNS('http://www.w3.org/2000/svg', name);
    for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, String(value));
    return node;
  };

  /**
   * Насколько створка может отъехать.
   *
   * Считаем ход в обе стороны по своей полосе и берём тот, где его больше:
   * от двери ждут, что она откроет проём, а не дёрнется на сантиметр.
   */
  function doorTravel(index, positions, doorWidth, openingWidth) {
    const count = positions.length;
    const rail = doorRail(index, count);
    const me = positions[index];
    let left = 0;
    let right = openingWidth - doorWidth;

    positions.forEach((x, other) => {
      if (other === index || doorRail(other, count) !== rail) return;
      if (x < me) left = Math.max(left, x + doorWidth);
      else right = Math.min(right, x - doorWidth);
    });

    const toLeft = me - left;
    const toRight = right - me;
    if (toLeft <= 1 && toRight <= 1) return 0;
    return toRight >= toLeft ? roundTo(right - me, 1) : roundTo(left - me, 1);
  }

  /**
   * Сдвигает створку.
   *
   * С доводчиком створка идёт дольше и в конце дотягивается — тормозит
   * почти до нуля и доходит сама. Без него доезжает ходом и коротко
   * отбивает от упора: разницу между этими двумя движениями и покупают.
   *
   * Конечное положение ставим сразу, а движение показываем поверх него.
   * Так створка оказывается там, где надо, даже если анимации не будет
   * вовсе — вкладка в фоне, включено «уменьшить движение», старый браузер.
   * Обратный порядок оставлял бы дверь висеть на полпути.
   */
  function slideDoor(group, from, to, soft) {
    /* Прошлое движение снимаем до всего остального. Анимация перебивает
       inline-стиль: незавершённая держит створку там, где её застали, и
       выставленное положение до элемента не доходит вовсе. Так дверь
       залипала после переключения вкладки — движение останавливалось
       на первом кадре и больше не отпускало. */
    group.getAnimations().forEach((run) => run.cancel());

    group.style.transform = `translateX(${to}px)`;
    // В фоне анимации не идут: там достаточно конечного положения.
    if (typeof group.animate !== 'function' || from === to || document.hidden) return;

    const at = (value) => ({transform: `translateX(${value}px)`});
    const back = to > from ? -12 : 12;
    const frames = soft
      ? [{...at(from), easing: 'cubic-bezier(.1,.86,.14,1)'}, at(to)]
      : [
          {...at(from), easing: 'cubic-bezier(.35,0,.85,1)'},
          {offset: 0.78, ...at(to), easing: 'ease-out'},
          {offset: 0.88, ...at(to + back), easing: 'ease-in'},
          {offset: 1, ...at(to)},
        ];
    const run = group.animate(frames, {duration: soft ? 1050 : 620});
    // Доигравшую снимаем сами: иначе она остаётся висеть на створке и
    // перебивает следующее положение.
    run.finished.then(() => run.cancel()).catch(() => {});
  }

  function drawDoors(host, quote, input, config) {
    host.textContent = '';
    const {geometry, profile} = quote;
    const width = input.openingWidth;
    const height = input.openingHeight;
    if (!(width > 0 && height > 0)) return;

    // Дверей стало меньше — забываем те, которых уже нет.
    for (const index of [...doorOpen]) {
      if (index >= input.doorCount) doorOpen.delete(index);
    }

    /* Размер задаём стилями, а не атрибутами: height="auto" у <svg>
       не значение длины, и браузер ругается на каждую перерисовку. */
    /* Ширину рамки подгоняем под саму картинку. viewBox с meet вписывает
       рисунок по высоте, и на высоком проёме по бокам оставались широкие
       тёмные поля — ниша выглядела втрое больше шкафа. */
    const frame = `${(width / height) * PREVIEW_HEIGHT_REM}rem`;
    host.style.maxWidth = frame;
    // Полоса створок идёт ровно под картинкой и той же ширины: иначе
    // подписи разъезжаются со своими дверями.
    const sashes = host.closest('[data-doors]')?.querySelector('[data-doors-sashes]');
    if (sashes) sashes.style.maxWidth = frame;

    const svg = svgEl('svg', {
      viewBox: `0 0 ${width} ${height}`,
      role: 'img',
      class: 'block h-auto w-full',
      preserveAspectRatio: 'xMidYMid meet',
    });
    const defs = svgEl('defs', {});
    svg.append(defs);

    /* Тень створки. Нахлёст у раздвижной двери — двадцать миллиметров
       на без малого двухметровый проём: волосяная линия, которую на картинке
       не разглядеть. Тень показывает, какая створка идёт впереди, и делает
       шов видимым — ровно за этим она и в макете.

       Размеры в миллиметрах проёма, а не в пикселях: картинка тянется,
       и тень обязана тянуться вместе с ней. Область фильтра расширена —
       по умолчанию она обрезала бы размытие по краю створки. */
    const shadow = svgEl('filter', {
      id: 'door-shadow',
      x: '-25%', y: '-15%', width: '150%', height: '130%',
      'color-interpolation-filters': 'sRGB',
    });
    shadow.append(svgEl('feDropShadow', {
      dx: 0, dy: 18, stdDeviation: 26,
      'flood-color': '#000000', 'flood-opacity': '0.42',
    }));
    defs.append(shadow);

    // Блик: одна полоса света на все стеклянные секции.
    const gloss = svgEl('linearGradient', {id: 'door-gloss', x1: '0', y1: '0', x2: '0.9', y2: '1'});
    gloss.append(svgEl('stop', {offset: '0', 'stop-color': '#ffffff', 'stop-opacity': '.55'}));
    gloss.append(svgEl('stop', {offset: '.42', 'stop-color': '#ffffff', 'stop-opacity': '.06'}));
    gloss.append(svgEl('stop', {offset: '.58', 'stop-color': '#000000', 'stop-opacity': '.04'}));
    gloss.append(svgEl('stop', {offset: '1', 'stop-color': '#ffffff', 'stop-opacity': '.3'}));
    defs.append(gloss);

    // Проём: тёмная ниша за дверями, чтобы отъехавшая створка было видно.
    svg.append(svgEl('rect', {x: 0, y: 0, width, height, fill: '#191817'}));

    /* Створки ставим слева направо: на стыке разных полос одна заходит на
       другую, на стыке одной полосы они сходятся встык. Раньше двери просто
       делили проём поровну, и нахлёст приходился на каждый стык — посередине
       ложилась тень от нахлёста, которого там нет. */
    const doorWidth = geometry.doorWidth;
    /* Нахлёст на стыке — всегда во весь профиль: наполовину створка на
       створку не заходит. Створок выходит ровно на проём: ширина полотна
       считалась из того же числа стыков. */
    const lapped = new Set(railSeams(input.doorCount));
    const positions = [];
    let edge = 0;
    input.doors.forEach((_, index) => {
      if (index > 0) edge += doorWidth - (lapped.has(index) ? geometry.overlapStep : 0);
      positions.push(edge);
    });

    /* Боковины — это ручка, и от её выбора ширина действительно меняется:
       берём тот самый отступ, на который наполнение отходит от края
       полотна. А перемычки сверху и снизу — отдельные профили, одни на все
       семейства, и от ручки не зависят. Раньше верх рисовался отступом
       профиля, и при смене ручки ехала перемычка. */
    const side = Math.max(8, geometry.fillingInset || 10);
    const top = Math.max(8, Number(config.frameTopMm) || 20);
    const bottom = Math.max(top, Number(config.frameBottomMm) || 90);
    const paint = profile.paint || '#C6C9CB';

    /* След уехавшей створки — на её прежнем месте.

       Задняя полоса уходит ровно за переднюю: на четырёх дверях крайние
       встают под центральные и пропадают из виду целиком. Так оно и в
       шкафу, но на картинке это читается как поломка — двери исчезли. Пунктирный след с номером говорит, где створка
       стояла и куда делась, и ничего при этом не выдумывает: сама створка
       нарисована там, куда доехала.

       По следу и возвращают: нажатие на пустой проём закрывает ту дверь,
       которая тут стояла. Искать её под соседней створкой не нужно —
       да и не выйдет, она под ней целиком.

       Следы лежат ПОД створками: если проём успела занять другая дверь,
       нажатие достаётся ей, а не следу. И рисуются сразу все, а показываются
       по месту — нажатие двигает створку без перерисовки картинки, иначе
       сбивалась бы анимация. */
    positions.forEach((x, index) => {
      const ghost = svgEl('g', {
        'data-door-ghost': index + 1,
        tabindex: 0,
        style: 'cursor: pointer',
      });
      ghost.style.display = doorOpen.has(index) ? '' : 'none';

      // Ловим нажатие всей площадью проёма, а не одной пунктирной линией.
      ghost.append(svgEl('rect', {
        x, y: 0, width: doorWidth, height,
        fill: 'none', 'pointer-events': 'all',
      }));
      ghost.append(svgEl('rect', {
        x: x + 6, y: 6, width: doorWidth - 12, height: height - 12,
        fill: 'none', stroke: 'rgba(255,255,255,.32)', 'stroke-width': 4,
        'stroke-dasharray': '26 22', 'pointer-events': 'none',
      }));
      const mark = svgEl('text', {
        x: x + doorWidth / 2,
        y: height - bottom / 2,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
        'font-size': Math.max(38, bottom * 0.5),
        fill: 'rgba(255,255,255,.38)',
        'pointer-events': 'none',
      });
      mark.textContent = String(index + 1);
      ghost.append(mark);
      svg.append(ghost);
    });

    /* Задняя полоса рисуется первой: иначе отъехавшая створка уезжала бы
       поверх соседней, а в шкафу она уходит за неё. */
    const order = input.doors
      .map((door, index) => ({door, index}))
      .sort((a, b) => doorRail(b.index, input.doorCount) - doorRail(a.index, input.doorCount));

    for (const {door, index} of order) {
      const x = positions[index];
      const travel = doorTravel(index, positions, doorWidth, width);
      /* Доводчик у каждой створки свой, поэтому и движение своё: одна дверь
         может дотягиваться, соседняя — отбивать от упора. */
      const group = svgEl('g', {
        'data-door': index + 1,
        'data-travel': travel,
        'data-rail': doorRail(index, input.doorCount) === 0 ? 'front' : 'back',
        'data-soft': door.closer ? 'true' : 'false',
        tabindex: travel ? 0 : -1,
        filter: 'url(#door-shadow)',
        style: 'cursor: pointer',
      });
      if (doorOpen.has(index)) group.style.transform = `translateX(${travel}px)`;

      const caption = svgEl('title', {});
      caption.textContent = String(index + 1);
      group.append(caption);

      // Рамка полотна: ручка по бокам, перемычки сверху и снизу.
      group.append(svgEl('rect', {
        x, y: 0, width: doorWidth, height,
        fill: paint, stroke: 'rgba(0,0,0,.4)', 'stroke-width': 2,
      }));

      const innerX = x + side;
      const innerY = top;
      const innerW = Math.max(0, doorWidth - side * 2);
      const innerH = Math.max(0, height - top - bottom);

      const rows = door.split === 'rows';
      const along = rows ? innerH : innerW;
      const layout = sectionSizes(door, along);
      const raw = layout.sizes.reduce((sum, v) => sum + v, 0);
      const scale = raw > 0 ? along / raw : 1;

      let cursor = 0;
      const seams = [];
      door.sections.forEach((section, at) => {
        const size = layout.sizes[at] * scale;
        const rect = rows
          ? {x: innerX, y: innerY + cursor, width: innerW, height: size}
          : {x: innerX + cursor, y: innerY, width: size, height: innerH};

        const fill = fillOf(section.fill, config);
        if (fill && fill.image) {
          const clipId = `door-${index}-${at}`;
          const clip = svgEl('clipPath', {id: clipId});
          clip.append(svgEl('rect', rect));
          defs.append(clip);
          group.append(svgEl('image', {
            ...rect,
            href: fill.image,
            preserveAspectRatio: 'xMidYMid slice',
            'clip-path': `url(#${clipId})`,
          }));
        } else if (fill && fill.tint) {
          // Крашеное стекло: ровный цвет и блик поверх.
          group.append(svgEl('rect', {...rect, fill: fill.tint}));
          group.append(svgEl('rect', {...rect, fill: 'url(#door-gloss)'}));
        } else {
          const id = `mirror-${index}-${at}`;
          const mirror = svgEl('linearGradient', {id, x1: '0', y1: '0', x2: '1', y2: '1'});
          mirror.append(svgEl('stop', {offset: '0', 'stop-color': '#e8eef1'}));
          mirror.append(svgEl('stop', {offset: '.45', 'stop-color': '#f7fafb'}));
          mirror.append(svgEl('stop', {offset: '.55', 'stop-color': '#d5dee3'}));
          mirror.append(svgEl('stop', {offset: '1', 'stop-color': '#eef3f5'}));
          defs.append(mirror);
          group.append(svgEl('rect', {...rect, fill: `url(#${id})`}));
        }
        group.append(svgEl('rect', {...rect, fill: 'none', stroke: 'rgba(0,0,0,.2)', 'stroke-width': 1}));

        cursor += size;
        if (at < door.sections.length - 1) seams.push(cursor);
      });

      /* Разделители кладём поверх секций, а не между ними: разделитель
         сидит на шве и заходит в обе половины, поэтому нарисованный
         в очередь он оказался бы наполовину закрыт следующей секцией. */
      const bar = quote.dividerWidth * scale;
      for (const seam of seams) {
        const slot = rows
          ? {x: innerX, y: innerY + seam - bar / 2, width: innerW, height: bar}
          : {x: innerX + seam - bar / 2, y: innerY, width: bar, height: innerH};
        group.append(svgEl('rect', {...slot, fill: paint, stroke: 'rgba(0,0,0,.25)', 'stroke-width': 1}));
      }

      // Номер створки — на нижней перемычке, где для него есть место.
      const badge = svgEl('text', {
        x: x + doorWidth / 2,
        y: height - bottom / 2,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
        'font-size': Math.max(38, bottom * 0.5),
        fill: 'rgba(0,0,0,.45)',
      });
      badge.textContent = String(index + 1);
      group.append(badge);

      svg.append(group);
    }

    host.append(svg);
  }

  /* ------------------------------------------------- отрисовка страницы */

  /** «Дверь 2» — словами страницы: в скрипте переводов нет. */
  const doorName = (box, index) => (box.dataset.doorN || '').replace('{n}', String(index + 1));

  /** Вкладки дверей. Их столько, сколько дверей в проёме. */
  function renderTabs(box, input) {
    const host = $('[data-doors-tabs]', box);
    if (!host) return;

    host.textContent = '';
    for (let index = 0; index < input.doorCount; index += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.doorsTab = String(index);
      button.className =
        'whitespace-nowrap rounded-sm px-4 py-2.5 text-[0.8125rem] font-bold transition-colors duration-200 ' +
        (index === activeDoor
          ? 'bg-night text-paper'
          : 'text-subtle hover:bg-surface hover:text-ink-strong');
      button.textContent = doorName(box, index);
      host.append(button);
    }
    // В режиме «все одинаковые» вкладки не нужны: дверь одна на всех.
    host.hidden = input.sameDoors || input.doorCount < 2;
  }

  /**
   * Строки секций.
   *
   * Перерисовываем только когда меняется состав — число секций, дверь,
   * наполнение. Размеры правятся на месте: перерисовка на каждую цифру
   * уводила бы курсор из поля на первом же нажатии.
   */
  function renderSections(box, input, config, layout) {
    const host = $('[data-doors-sections]', box);
    const template = $('[data-doors-section-template]', box);
    if (!host || !template) return;

    const door = input.doors[activeDoor];
    const signature = JSON.stringify([activeDoor, activeSection, door.sections.map((s) => s.fill)]);
    if (host.dataset.signature !== signature) {
      host.dataset.signature = signature;
      host.textContent = '';
      door.sections.forEach((section, at) => {
        const row = template.content.firstElementChild.cloneNode(true);
        row.dataset.doorsIndex = String(at);
        host.append(row);
      });
    }

    $$('[data-doors-section]', host).forEach((row, at) => {
      const section = door.sections[at];
      const fill = fillOf(section.fill, config);
      const last = at === door.sections.length - 1;

      row.classList.toggle('bg-shell', at === activeSection);
      row.classList.toggle('shadow-[inset_3px_0_0_var(--color-ink-strong)]', at === activeSection);

      const swatch = $('[data-doors-swatch]', row);
      if (swatch) {
        swatch.style.backgroundImage = fill && fill.image ? `url("${fill.image}")` : '';
        swatch.style.backgroundColor = fill && fill.image ? '' : '#dfe6ea';
      }
      const name = $('[data-doors-section-name]', row);
      if (name) name.textContent = fill ? fill.label : '';
      const hint = $('[data-doors-section-hint]', row);
      if (hint) hint.textContent = `${Math.round(layout.sizes[at])} × ${Math.round(layout.across)} mm`;

      const size = $('[data-doors-section-size]', row);
      // Не перебиваем то, что покупатель печатает прямо сейчас.
      if (size && document.activeElement !== size) size.value = String(Math.round(layout.sizes[at]));

      const remove = $('[data-doors-remove]', row);
      if (remove) remove.hidden = door.sections.length < 2;
    });

    /* Делить нечего — кнопку гасим. Секция меньше минимума не бывает,
       и вместо неё выходила бы вторая того же размера из ниоткуда. */
    const add = $('[data-doors-add]', box);
    if (add) {
      const last = layout.sizes[layout.sizes.length - 1] || 0;
      add.disabled = door.sections.length >= MAX_SECTIONS || last / 2 < MIN_SECTION;
      add.classList.toggle('opacity-40', add.disabled);
      add.classList.toggle('pointer-events-none', add.disabled);
    }
  }

  /**
   * Полоса створок под картинкой.
   *
   * Показывает состав каждой двери — сколько секций, есть ли доводчик —
   * и переключает ту, которую правят. Нажатие снимает «все двери
   * одинаковые»: одна створка выбирают только затем, чтобы сделать её
   * не такой, как остальные.
   */
  /**
   * Плашки полос: у какой створки какая.
   *
   * Отдельно от вкладок дверей: вкладки переключают, что правят, а тут
   * у каждой створки своё положение сразу, и сравнивать их надо разом.
   * Строка живёт и при «все двери одинаковые» — наполнение у дверей
   * общее, а полоса у каждой своя.
   */
  function renderRails(box, input) {
    const host = $('[data-doors-rails]', box);
    if (!host) return;

    host.style.gridTemplateColumns = `repeat(${input.doorCount}, minmax(0, 1fr))`;
    host.textContent = '';

    input.doors.forEach((_, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.doorsRail = String(index);

      const name = document.createElement('span');
      name.className = 'block text-[0.8125rem] font-bold';
      name.textContent = doorName(box, index);

      const note = document.createElement('span');
      note.dataset.doorsRailNote = '';
      note.className = 'mt-0.5 block truncate text-[0.6875rem] font-medium';

      button.append(name, note);
      host.append(button);
      paintRail(button, doorRail(index, input.doorCount) === 0, host);
    });
  }

  /** Вид плашки: тёмная — створка впереди, она и закрывает соседние. */
  function paintRail(button, front, host) {
    button.dataset.state = front ? 'front' : 'back';
    button.className =
      'min-w-0 rounded-md border px-1.5 py-2.5 text-center transition-colors duration-200 ' +
      (front
        ? 'border-night bg-night text-paper'
        : 'border-rule-strong bg-paper hover:border-rule-firm');
    const note = button.querySelector('[data-doors-rail-note]');
    if (note) note.textContent = (front ? host.dataset.front : host.dataset.back) || '';
  }

  function renderSashes(box, input, config) {
    const host = $('[data-doors-sashes]', box);
    if (!host) return;

    host.style.gridTemplateColumns = `repeat(${input.doorCount}, minmax(0, 1fr))`;
    host.textContent = '';

    input.doors.forEach((door, index) => {
      const closer = (config.closers || []).find((item) => item.handle === door.closer);
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.doorsSash = String(index);
      /* Подписи держим на самой плашке: открывание переключает её на месте,
         без перерисовки картинки, иначе сбивалась бы анимация. */
      button.dataset.shut = closer
        ? host.dataset.withCloser || ''
        : (host.dataset.sections || '').replace('{n}', String(door.sections.length));
      button.dataset.open = host.dataset.opened || '';

      const name = document.createElement('span');
      name.className = 'block text-[0.8125rem] font-bold';
      name.textContent = doorName(box, index);

      const note = document.createElement('span');
      note.dataset.doorsSashNote = '';
      note.className = 'mt-0.5 block truncate text-[0.6875rem] font-medium';

      button.append(name, note);
      host.append(button);
      paintSash(button, doorOpen.has(index));
    });
  }

  /**
   * Вид плашки: тёмная — створка отъехала.
   *
   * Открывание живёт здесь, а не на самой створке: внутри неё теперь секции,
   * и одно нажатие не может значить сразу два разных действия. Плашка ещё и
   * достаётся всегда — уехавшую за соседнюю дверь не нажать, её не видно.
   */
  function paintSash(button, opened) {
    button.dataset.state = opened ? 'open' : 'shut';
    button.className =
      'min-w-0 rounded-md border px-1.5 py-2.5 text-center transition-colors duration-200 ' +
      (opened
        ? 'border-night bg-night text-paper'
        : 'border-rule-strong bg-paper hover:border-rule-firm');
    const note = button.querySelector('[data-doors-sash-note]');
    if (note) {
      note.textContent = (opened ? button.dataset.open : button.dataset.shut) || '';
      note.classList.toggle('text-night-text', opened);
      note.classList.toggle('text-muted', !opened);
    }
  }

  function paintDoors() {
    const box = $('[data-doors]');
    const settingsNode = $('[data-doors-settings]');
    if (!box || !settingsNode) return;

    let config;
    try {
      config = JSON.parse(settingsNode.textContent);
    } catch {
      return;
    }

    const number = (name, fallback) => {
      const field = box.querySelector(`[data-doors-field="${name}"]`);
      const [min, max] = doorLimits(name);
      return Math.round(clampNumber(Number(field ? field.value : fallback), min, max));
    };

    const doorCount = number('doorCount', 2);
    const input = {
      openingWidth: number('openingWidth', 1800),
      openingHeight: number('openingHeight', 2500),
      doorCount,
      /* Нахлёстов столько, сколько стыков с разными полосами. Поля для них
         нет: нахлёст получается там, где соседи стоят на разных полосах,
         а на одной сходятся встык, — значит, число следует из «Порядка
         створок», и вводить его отдельно не только незачем, но и не из
         чего: заказанное сверх раскладки рисовать было бы негде. */
      overlapCount: railSeams(doorCount).length,
      profileHandle: box.querySelector('[data-doors-field="profileHandle"]:checked')?.value || '',
      sameDoors: Boolean(box.querySelector('[data-doors-field="sameDoors"]')?.checked),
    };

    input.doors = ensurePlan(input, config);

    const quote = doorQuote(input, config);
    if (!quote) return;
    quote.config = config;

    const out = (name, text) => {
      const node = box.querySelector(`[data-doors-out="${name}"]`);
      if (node) node.textContent = text;
    };

    /* Цвет меняем только у выбранной карточки, остальные держим в серебре:
       у семейства без выбранного цвета оставался его первый, и ряд читался
       как ошибка. */
    $$('[data-doors-family-image]', box).forEach((shot) => {
      const family = shot.dataset.doorsFamilyImage;
      const wanted = family === quote.profile.family ? quote.profile.colour : 'silver';
      const row =
        config.profiles.find((item) => item.family === family && item.colour === wanted) ||
        config.profiles.find((item) => item.family === family);
      if (row && row.image && shot.getAttribute('src') !== row.image) shot.src = row.image;
    });

    // Направление разделителей — у той двери, которую правят.
    const door = input.doors[activeDoor];
    const splitField = box.querySelector(`[data-doors-field="split"][value="${door.split}"]`);
    if (splitField && !splitField.checked) splitField.checked = true;

    const rows = door.split === 'rows';
    const along = rows ? quote.geometry.fillingHeight10 : quote.geometry.fillingWidth10;
    const across = rows ? quote.geometry.fillingWidth10 : quote.geometry.fillingHeight10;
    const layout = sectionSizes(door, along);
    layout.across = across;
    /* Посчитанное кладём обратно на секции. Дальше и «добавить», и «убрать»,
       и правка числа отталкиваются от готовых размеров, а не от «ноль значит
       остаток»: с остатком сумма разъезжалась после каждой из них. */
    door.sizes = layout.sizes;
    door.sections.forEach((section, at) => {
      section.size = layout.sizes[at];
    });

    renderTabs(box, input);
    renderSections(box, input, config, layout);

    /* Выбор материала относится к выбранной секции: показываем, к какой. */
    syncMaterialPicker(box, door.sections[activeSection].fill);

    /* Подписи «к чему это относится». Пока двери одинаковые, правится одна
       на всех, и называть её «дверь 1» было бы враньём — покупатель правит
       все сразу. */
    const scope = input.sameDoors
      ? box.dataset.scopeAll || ''
      : (box.dataset.scopeDoor || '').replace('{n}', String(activeDoor + 1));
    ['scopeDividers', 'scopeCloser', 'scopeSections'].forEach((name) => out(name, scope));
    out(
      'selLabel',
      `${input.sameDoors ? box.dataset.scopeAllPlain || '' : doorName(box, activeDoor)} · ` +
        (box.dataset.sectionN || '').replace('{n}', String(activeSection + 1)),
    );

    const {geometry} = quote;
    out('doorWidth', `${geometry.doorWidth} × ${geometry.handleLength} mm`);
    out('handleLength', `${geometry.handleLength} mm`);
    out('filling10', `${geometry.fillingWidth10} × ${geometry.fillingHeight10} mm`);
    out('filling4', `${geometry.fillingWidth4} × ${geometry.fillingHeight4} mm`);
    out('area', `${quote.materialArea} m²`);
    out('dividers', String(quote.cut.dividers));

    /* Итог шага — в шапке его карточки: покупателю не нужно листать вниз,
       чтобы проверить, что он только что сделал. */
    out('sashSummary', (box.dataset.sashSummary || '').replace('{w}', String(geometry.doorWidth)));
    out(
      'profileSummary',
      `${quote.profile.family} · ${quote.profile.colourLabel || quote.profile.colour} · ` +
        (box.dataset.sideSummary || '').replace('{n}', String(geometry.fillingInset)),
    );
    out(
      'fillingSummary',
      (box.dataset.fillingSummary || '')
        .replace('{w}', String(Math.round(rows ? across : along)))
        .replace('{h}', String(Math.round(rows ? along : across))),
    );
    out(
      'previewSummary',
      `${input.openingWidth} × ${input.openingHeight} mm · ` +
        (box.dataset.doorsN || '').replace('{n}', String(input.doorCount)),
    );
    out('brushPrice', quote.brushCost > 0 ? formatMoney(Math.round(quote.brushCost * 100)) : '—');

    /* Что стоит за суммой — рядом с самой суммой. Число дверей берём той же
       строкой словаря, что и подпись предпросмотра, а размер створки той же,
       что и первая строка раскроя: иначе одно и то же место в двух углах
       страницы называлось бы по-разному. */
    out('priceDoors', (box.dataset.doorsN || '').replace('{n}', String(input.doorCount)));
    out('priceSize', `${geometry.doorWidth} × ${geometry.handleLength} mm`);

    /* Сколько нахлёстов вышло — под теми плашками, которые их и задают.
       А если все створки оказались на одной полосе, разъехаться им негде,
       и сказать об этом надо там же: смета посчитается, а шкаф не откроется. */
    const stackNote = $('[data-doors-stacking-note]', box);
    if (stackNote) {
      const dead = input.doorCount > 1 && input.overlapCount === 0;
      stackNote.textContent = dead
        ? stackNote.dataset.none || ''
        : `${(stackNote.dataset.made || '').replace('{n}', String(input.overlapCount))} ` +
          `${stackNote.dataset.hint || ''}`;
      stackNote.classList.toggle('text-danger', dead);
      stackNote.classList.toggle('text-faint', !dead);
    }

    /* Сколько всего декоров и какой выбран — над полосой карточек. */
    const plateCountNode = $('[data-doors-out="plateCount"]', box);
    if (plateCountNode) {
      const chosen = (config.plates || []).find(
        (row) => row.handle === box.querySelector('[data-doors-field="plateHandle"]:checked')?.value,
      );
      out(
        'plateCount',
        (box.dataset.plateCount || '')
          .replace('{n}', String((config.plates || []).length))
          .replace('{decor}', chosen ? chosen.decor || chosen.label : '—'),
      );
    }

    /* Своё наполнение: под полями пишем, что вышло за квадрат. Цену за лист
       в уме на площадь никто не делит, а по этому числу сразу видно, не
       ошибся ли покупатель запятой. */
    const customNode = $('[data-doors-out="customRate"]', box);
    if (customNode) {
      const chosen = readMaterialPicker(box);
      const custom = String(chosen).startsWith(CUSTOM_CHOICE) ? customFill(chosen) : null;
      if (custom) {
        out(
          'customRate',
          custom.incomplete
            ? doorsText('customEmpty')
            : doorsText('customRate').replace('{p}', formatRate(custom.rate)),
        );
      }
    }

    /* Плиты: строка на декор. Их может быть несколько — по одной на каждый,
       и лист между декорами не делится. */
    const plateHost = $('[data-doors-plates-out]', box);
    if (plateHost) {
      plateHost.textContent = '';
      for (const row of quote.plateLines) {
        const line = document.createElement('div');
        line.className = 'flex items-baseline justify-between gap-4';
        const term = document.createElement('dt');
        term.className = 'font-medium text-muted';
        term.textContent = row.plate.decor || row.plate.label;
        const value = document.createElement('dd');
        value.className = 'num text-[0.875rem] text-ink-strong';
        /* В строке декора важнее денег ничего нет: доля листа сама по себе
           не говорит, во что обошлась плита, и «¼» рядом с «1» читается как
           мелочь, хотя разница в сотню евро. */
        value.textContent = row.plan.fits
          ? `${plateFraction(row.plan.plates)} · ${formatMoney(Math.round(row.plan.plates * row.plate.price * 100))}`
          : '—';
        line.append(term, value);
        plateHost.append(line);
      }
      plateHost.hidden = quote.plateLines.length === 0;
    }

    const warning = box.querySelector('[data-doors-warning]');
    if (warning) warning.hidden = !quote.blocked;
    out('total', quote.blocked ? '—' : formatMoney(Math.round(quote.total * 100)));

    const preview = $('[data-doors-preview]', box);
    if (preview) drawDoors(preview, quote, input, config);
    renderSashes(box, input, config);
    renderRails(box, input);

    /* Подсказки про движение: у створки с доводчиком оно другое, и сказать
       об этом надо там, где его выбирают, и там, где на него смотрят. */
    const anySoft = input.doors.some((item) => item.closer);
    const hint = $('[data-doors-preview-hint]', box);
    if (hint) hint.textContent = (anySoft ? hint.dataset.soft : hint.dataset.plain) || '';
    const closerHint = $('[data-doors-closer-hint]', box);
    if (closerHint) {
      closerHint.textContent = (door.closer ? closerHint.dataset.soft : closerHint.dataset.plain) || '';
    }

    /* Выпадающий список показывает доводчик той двери, которую правят, и
       только те, что на неё встанут. Пока двери одинаковые, доводчик идёт
       сразу на все, поэтому подойти он должен каждой: крайнему на средней
       створке упереться не во что.

       Не подошедшие прячем, а не убираем: список собран разметкой, и
       пересобирать его на каждую перерисовку — терять открытый список
       под курсором. */
    const closerField = $('[data-doors-field="closerHandle"]', box);
    let hidden = 0;
    if (closerField) {
      for (const option of closerField.options) {
        if (!option.value) continue;
        const placement = placementOf(option.value, config);
        const fits = input.sameDoors
          ? input.doors.every((_, at) => closerFits(placement, at, input.doors.length))
          : closerFits(placement, activeDoor, input.doors.length);
        option.hidden = !fits;
        option.disabled = !fits;
        if (!fits) hidden += 1;
      }
      if (closerField.value !== (door.closer || '')) closerField.value = door.closer || '';
    }

    /* Правило показываем только когда оно что-то отняло: на двух дверях
       отнимать нечего, и объяснять там нечего тоже. */
    const closerRule = $('[data-doors-closer-rule]', box);
    if (closerRule) closerRule.hidden = hidden === 0;

    /* Расчёт для заявки. Подписи берём со страницы, а не из словаря:
       theme.js — статический файл, фильтра t в нём нет. */
    const summary = [];

    /* Подпись поля берём с самого поля, помеченного в разметке, а не
       у ближайшего label: у счётчика дверей обёртка — div, и по label
       он не находился вовсе. Мастерская получала заявку без числа
       дверей: ширину полотна там видели, а сколько их — угадывали. */
    const askedFor = (name) => {
      const field = $(`[data-doors-field="${name}"]`, box);
      const wrap = field && field.closest('[data-doors-ask]');
      const caption = wrap && $('[data-doors-caption]', wrap);
      if (!field || !caption) return;
      if (Number(field.value) === 0) return;
      summary.push(`${caption.textContent.trim()}: ${field.value}`);
    };

    const titleOf = (key) => {
      const node = $(`[data-doors-title="${key}"]`, box);
      return node ? node.textContent.trim() : key;
    };

    ['openingWidth', 'openingHeight', 'doorCount'].forEach(askedFor);
    summary.push((stackNote?.dataset.made || '').replace('{n}', String(input.overlapCount)));
    summary.push(`${titleOf('profile')}: ${quote.profile.label}`);

    /* Состав дверей — построчно. Одинаковые двери описываем один раз:
       «Дверь 1, Дверь 2, Дверь 3» с тем же содержимым читать невозможно. */
    summary.push(`${titleOf('composition')}:`);
    const described = input.sameDoors ? [input.doors[0]] : input.doors;
    /* Размеры берём из раскроя, а не из списка слева: в списке место
       секции, а мастерской нужна деталь — уже без того, что забрал
       разделитель. По этим числам и режут. */
    described.forEach((item, index) => {
      const from = input.sameDoors ? 0 : index;
      const head = input.sameDoors ? '' : `  ${doorName(box, index)}:`;
      if (head) summary.push(head);
      item.sections.forEach((section, at) => {
        const panel = quote.cut.panels.find((row) => row.door === from && row.section === at);
        if (!panel) return;
        summary.push(`  ${at + 1}. ${panel.fill.label} — ${Math.round(panel.w)} × ${Math.round(panel.h)} mm`);
      });
    });

    /* Уплотнитель идёт в каждой двери, но в сообщении назван всё равно:
       цех читает его как список того, что кладут в дверь, а не как
       перечень доплат. Подпись берём из сметы — своего словаря
       у скрипта нет. */
    const brushLabel = $('[data-doors-out="brushPrice"]', box)
      ?.closest('div')
      ?.querySelector('dt')
      ?.textContent.trim();
    if (brushLabel) summary.push(brushLabel);
    /* Доводчики — по створкам: у каждой свой, и «доводчик: 2» ничего
       не сказало бы о том, на каких он дверях. */
    const describedDoors = input.sameDoors ? input.doors.slice(0, 1) : input.doors;
    describedDoors.forEach((item, index) => {
      const closer = (config.closers || []).find((row) => row.handle === item.closer);
      if (!closer) return;
      summary.push(input.sameDoors ? closer.label : `${doorName(box, index)}: ${closer.label}`);
    });

    $$('dl [data-doors-out], dl dt', box).forEach((node) => {
      if (!node.dataset.doorsOut) return;
      if (node.dataset.doorsOut === 'filling4' || node.dataset.doorsOut === 'handleLength') return;
      if (node.closest('[hidden]')) return;
      const term = node.previousElementSibling;
      if (term) summary.push(`${term.textContent.trim()}: ${node.textContent.trim()}`);
    });
    for (const row of quote.plateLines) {
      if (!row.plan.fits) continue;
      summary.push(`${row.plate.label}: ${plateFraction(row.plan.plates)}`);
    }

    const priceNode = $('[data-doors-out="total"]', box);
    const priceCaption = priceNode && priceNode.parentElement.querySelector('.tick');
    summary.push(
      `${priceCaption ? priceCaption.textContent.trim() : ''}: ${priceNode ? priceNode.textContent.trim() : ''}`,
    );

    doorSummary = summary.join('\n');

    const request = box.querySelector('[data-doors-request]');
    if (request) request.hidden = quote.blocked;
    fillDoorRequest();
  }

  /** Ставит переключатели материала на то, что выбрано у активной секции. */
  function syncMaterialPicker(box, fill) {
    const isPlate = String(fill).startsWith(PLATE_CHOICE + ':');
    const isCustom = String(fill).startsWith(CUSTOM_CHOICE + ':') || fill === CUSTOM_CHOICE;
    let choice = fill;
    if (isPlate) choice = PLATE_CHOICE;
    if (isCustom) choice = CUSTOM_CHOICE;

    const pill = box.querySelector(`[data-doors-field="materialHandle"][value="${choice}"]`);
    if (pill && !pill.checked) pill.checked = true;

    if (isPlate) {
      const handle = String(fill).slice(PLATE_CHOICE.length + 1);
      const card = box.querySelector(`[data-doors-field="plateHandle"][value="${handle}"]`);
      if (card && !card.checked) card.checked = true;
    }

    /* Числа своего наполнения хранятся в строке секции, а не в полях: секций
       несколько, и у каждой они свои. Переключились на секцию — поля обратно
       заполняются из её строки. */
    if (isCustom) {
      const field = box.querySelector('[data-doors-field="customSquarePrice"]');
      if (field) field.value = customParts(fill).squarePrice;
    }

    const plates = box.querySelector('[data-doors-plates]');
    if (plates) plates.hidden = !isPlate;
    const custom = box.querySelector('[data-doors-custom]');
    if (custom) custom.hidden = !isCustom;
  }

  /** Что выбрано в переключателях материала — одной строкой для секции. */
  function readMaterialPicker(box) {
    const pill = box.querySelector('[data-doors-field="materialHandle"]:checked')?.value || '';

    if (pill === PLATE_CHOICE) {
      const card = box.querySelector('[data-doors-field="plateHandle"]:checked')?.value || '';
      return card ? `${PLATE_CHOICE}:${card}` : pill;
    }

    if (pill === CUSTOM_CHOICE) {
      const price = (box.querySelector('[data-doors-field="customSquarePrice"]')?.value || '').trim();
      return `${CUSTOM_CHOICE}:${price}`;
    }

    return pill;
  }

  /* ------------------------------------------------------ заявка на дверь

     Расчёт вписываем в форму Shopify Forms сами: скрытых полей и подстановки
     из адреса приложение не знает — оно подставляет только имя и адрес
     вошедшего покупателя. Поле лежит в открытом теневом дереве, поэтому
     дотянуться до него можно.

     Значение приложение принимает как набор с клавиатуры: поле внутри
     неуправляемое, с подпиской на ввод. Шлём и input, и change — какое из них
     слушает форма, зависит от её сборки, а лишнее ей не мешает. */

  let doorSummary = '';
  /* Что вписали в прошлый раз: дописанное покупателем затирать нельзя. */
  let doorFilled = '';

  function fillDoorRequest() {
    const embed = $('[data-doors-request-form] shopify-forms-embed');
    const field = embed?.shadowRoot?.querySelector('textarea');
    if (!field || !doorSummary) return false;

    if (field.value && field.value !== doorFilled) return true;

    field.value = doorSummary;
    doorFilled = doorSummary;
    field.dispatchEvent(new Event('input', {bubbles: true}));
    field.dispatchEvent(new Event('change', {bubbles: true}));
    return true;
  }

  /* Форма приезжает запросом и в дереве появляется позже нажатия — поэтому
     ждём её, а не пробуем вписать расчёт один раз. */
  on('click', '[data-doors-request]', (_event, button) => {
    const panel = $('[data-doors-request-form]');
    if (!panel) return;

    panel.hidden = false;
    button.setAttribute('aria-expanded', 'true');

    // Липкий столбец с раскрытой формой в экран не помещается, и низ формы
    // становится недостижим: на время заявки отпускаем его.
    const aside = button.closest('aside');
    if (aside) aside.style.position = 'static';

    panel.scrollIntoView({behavior: 'smooth', block: 'center'});

    const deadline = Date.now() + 15000;
    const tick = () => {
      if (fillDoorRequest()) return;
      if (Date.now() < deadline) setTimeout(tick, 200);
    };
    tick();
  });

  /* Тип ручки и цвет — два шага одного выбора. Полосы цветов лежат
     в разметке все сразу, показана полоса выбранного типа: переключение
     не должно ходить на сервер. Радиогруппа у цветов одна на все полосы,
     поэтому выбор в новой полосе сам снимает старый — остаётся проверить,
     что в показанной полосе вообще что-то выбрано. */
  function syncDoorFamily(family) {
    const box = $('[data-doors]');
    if (!box) return;

    /* Цвет переносим на новый тип. Наборы у типов почти одинаковые, и терять
       выбор при смене формы незачем: выбрал чёрный у Haon, перешёл на Kari —
       остаётся чёрный. Раньше бралась первая метка в полосе, и цвет сбрасывался
       на каждом переключении. */
    const previous = $('[data-doors-field="profileHandle"]:checked', box)?.dataset.doorsColour;

    $$('[data-doors-colours]', box).forEach((panel) => {
      panel.hidden = panel.dataset.doorsColours !== family;
    });

    const panel = $(`[data-doors-colours="${family}"]`, box);
    if (!panel) return;
    if ($('[data-doors-field="profileHandle"]:checked', panel)) return;

    /* Нет такого цвета у нового типа — берём первый в полосе. Порядок полос
       один и тот же, так что это всегда серебро: оно есть у всех. */
    const same = previous
      ? $(`[data-doors-field="profileHandle"][data-doors-colour="${previous}"]`, panel)
      : null;
    const pick = same || $('[data-doors-field="profileHandle"]', panel);
    if (pick) pick.checked = true;
  }

  /* Тип берём от выбранного цвета, а не наоборот: разметка могла прийти
     с уже отмеченным профилем — из редактора тем или после «назад». */
  function initDoorFamily() {
    const box = $('[data-doors]');
    if (!box) return;
    const checked = $('[data-doors-field="profileHandle"]:checked', box);
    const panel = checked ? checked.closest('[data-doors-colours]') : null;
    const family = panel ? panel.dataset.doorsColours : $('[data-doors-family]', box)?.value;
    if (!family) return;
    const radio = $(`[data-doors-family][value="${family}"]`, box);
    if (radio) radio.checked = true;
    syncDoorFamily(family);
  }

  on('change', '[data-doors] [data-doors-family]', (event, field) => {
    syncDoorFamily(field.value);
    paintDoors();
  });

  on('input', '[data-doors] [data-doors-field]', (event, field) => {
    clampDoorField(field, false);
    if (field.dataset.doorsField === 'split') applySplit(field.value);
    if (field.dataset.doorsField === 'closerHandle') applyCloser(field.value);
    if (CUSTOM_FIELDS.has(field.dataset.doorsField)) {
      applyMaterial();
    }
    paintDoors();
  });
  on('change', '[data-doors] [data-doors-field]', (event, field) => {
    clampDoorField(field, true);
    if (field.dataset.doorsField === 'split') applySplit(field.value);
    if (field.dataset.doorsField === 'closerHandle') applyCloser(field.value);
    if (CUSTOM_FIELDS.has(field.dataset.doorsField)) {
      applyMaterial();
    }
    paintDoors();
  });

  /* Кнопки счётчиков правят то же поле, что и клавиатура: границы, зажим
     и пересчёт остаются одни на оба способа ввода. */
  on('click', '[data-doors-step]', (_event, button) => {
    const box = $('[data-doors]');
    const field = box && $(`[data-doors-field="${button.dataset.doorsStep}"]`, box);
    if (!field) return;
    const [min, max] = doorLimits(button.dataset.doorsStep);
    const next = clampNumber(Number(field.value) + Number(button.dataset.doorsBy), min, max);
    field.value = String(Math.round(next));
    paintDoors();
  });

  /* Створка под картинкой выбирает дверь для правки — и снимает «все
     одинаковые»: отдельную створку жмут ровно затем, чтобы сделать её
     не такой, как остальные. */
  /* Плашка под картинкой открывает и закрывает свою створку. */
  on('click', '[data-doors-sash]', (_event, button) => {
    const index = Number(button.dataset.doorsSash) || 0;
    const group = $(`[data-doors-preview] g[data-door="${index + 1}"]`);
    if (!group) return;
    toggleDoor(group);
    paintSash(button, doorOpen.has(index));
  });

  on('click', '[data-doors-close-all]', () => {
    doorOpen.clear();
    paintDoors();
  });

  /* ------------------------------------------------ правки конструктора

     Все они меняют план и зовут пересчёт: считать дёшево, а держать два
     пути обновления — картинку отдельно, цену отдельно — значит однажды
     показать одно, а посчитать другое. */

  const currentDoor = () => (doorPlan ? doorPlan[Math.min(activeDoor, doorPlan.length - 1)] : null);

  function applySplit(value) {
    const door = currentDoor();
    if (door) door.split = value === 'columns' ? 'columns' : 'rows';
  }

  /** Доводчик у той створки, которую правят. */
  function applyCloser(value) {
    const door = currentDoor();
    if (door) door.closer = value || '';
  }

  function applyMaterial() {
    const box = $('[data-doors]');
    const door = currentDoor();
    if (!box || !door) return;
    const fill = readMaterialPicker(box);
    if (fill) door.sections[activeSection].fill = fill;
  }

  /* Нажали на створку — она отъезжает и возвращается вторым нажатием.
     Открытые запоминаются: перерисовка картинки не должна их захлопывать. */
  function toggleDoor(group) {
    const index = Number(group.dataset.door) - 1;
    const travel = Number(group.dataset.travel) || 0;
    if (!travel) return;
    const opened = doorOpen.has(index);
    if (opened) doorOpen.delete(index);
    else doorOpen.add(index);

    const ghost = group.parentElement?.querySelector(`[data-door-ghost="${index + 1}"]`);
    if (ghost) ghost.style.display = opened ? 'none' : '';

    slideDoor(group, opened ? travel : 0, opened ? 0 : travel, group.dataset.soft === 'true');
  }

  /* Переставили створку на другую полосу. Перерисовываем всё: от полосы
     зависит и ход створки, и то, кто кого закрывает. */
  on('click', '[data-doors-rail]', (_event, button) => {
    const index = Number(button.dataset.doorsRail) || 0;
    railChoice.set(index, button.dataset.state === 'front' ? 1 : 0);
    paintDoors();
  });

  /* Нажали на створку — она отъезжает и возвращается вторым нажатием.
     Плашка под картинкой делает то же самое: её преимущество в том, что
     уехавшую за соседнюю дверь створку не нажать — её не видно. */
  function toggleDoorAndSash(group) {
    const index = Number(group.dataset.door) - 1;
    toggleDoor(group);
    const button = $(`[data-doors-sash="${index}"]`);
    if (button) paintSash(button, doorOpen.has(index));
  }

  on('click', '[data-doors-preview] [data-door]', (_event, group) => toggleDoorAndSash(group));
  on('keydown', '[data-doors-preview] [data-door]', (event, group) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    toggleDoorAndSash(group);
  });

  /* Нажали на пустой проём — вернули створку, которая там стояла. */
  function closeBySlot(ghost) {
    const number = Number(ghost.dataset.doorGhost);
    const group = ghost.parentElement?.querySelector(`g[data-door="${number}"]`);
    if (!group) return;
    toggleDoor(group);
    const button = $(`[data-doors-sash="${number - 1}"]`);
    if (button) paintSash(button, doorOpen.has(number - 1));
  }

  on('click', '[data-doors-preview] [data-door-ghost]', (_event, ghost) => closeBySlot(ghost));
  on('keydown', '[data-doors-preview] [data-door-ghost]', (event, ghost) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    closeBySlot(ghost);
  });
  on('click', '[data-doors-tab]', (_event, button) => {
    activeDoor = Number(button.dataset.doorsTab) || 0;
    activeSection = 0;
    paintDoors();
  });

  on('click', '[data-doors-pick]', (_event, button) => {
    const row = button.closest('[data-doors-section]');
    if (!row) return;
    activeSection = Number(row.dataset.doorsIndex) || 0;
    paintDoors();
  });

  /* ------------------------------------------ перетаскивание секций

     Секцию таскают за ручку, а не за строку целиком: в строке поле высоты
     и две кнопки, и попытка выделить число мышью утаскивала бы секцию.

     Соседи расступаются на высоту строки — так видно, куда встанет
     перетаскиваемая, и не нужен отдельный указатель места. Модель трогаем
     один раз, когда отпустили: перекладывать секции на каждое движение
     значило бы пересчитывать смету по десять раз в секунду.

     На указателях, а не на HTML5 drag-and-drop: тот на касании не работает
     вовсе, а калькулятор открывают и с телефона. */

  let sectionDrag = null;

  function endSectionDrag(apply) {
    if (!sectionDrag) return;
    const {rows, from, to} = sectionDrag;
    for (const row of rows) {
      row.style.transform = '';
      row.style.transition = '';
      row.style.position = '';
      row.style.zIndex = '';
      row.style.opacity = '';
    }
    sectionDrag = null;
    if (!apply || to === from) return;

    const door = currentDoor();
    if (!door) return;
    const [moved] = door.sections.splice(from, 1);
    door.sections.splice(to, 0, moved);

    /* Выбранная секция едет вместе с собой, а не остаётся на своём номере:
       иначе после переноса правится не та строка, на которую смотрят. */
    if (activeSection === from) activeSection = to;
    else if (from < activeSection && to >= activeSection) activeSection -= 1;
    else if (from > activeSection && to <= activeSection) activeSection += 1;

    paintDoors();
  }

  on('pointerdown', '[data-doors-grip]', (event, grip) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const row = grip.closest('[data-doors-section]');
    const host = row && row.parentElement;
    if (!row || !host) return;

    event.preventDefault();
    /* Захват указателя — удобство, а не условие: без него движение
       теряется, стоит курсору соскользнуть со строки. Но если браузер
       его не даёт, тащить всё равно можно. */
    try {
      grip.setPointerCapture(event.pointerId);
    } catch {
      /* пусто */
    }

    const rows = $$('[data-doors-section]', host);
    const at = rows.indexOf(row);
    sectionDrag = {
      rows,
      from: at,
      to: at,
      startY: event.clientY,
      height: row.getBoundingClientRect().height,
      row,
    };
    row.style.position = 'relative';
    row.style.zIndex = '3';
    row.style.opacity = '0.92';
  });

  on('pointermove', '[data-doors-grip]', (event) => {
    if (!sectionDrag) return;
    const {rows, from, height, row} = sectionDrag;
    const shift = event.clientY - sectionDrag.startY;
    row.style.transform = `translateY(${shift}px)`;

    const to = Math.max(0, Math.min(rows.length - 1, from + Math.round(shift / height)));
    if (to === sectionDrag.to) return;
    sectionDrag.to = to;

    rows.forEach((other, index) => {
      if (other === row) return;
      let move = 0;
      if (index > from && index <= to) move = -height;
      if (index < from && index >= to) move = height;
      other.style.transition = 'transform .16s var(--ease-out-soft, ease-out)';
      other.style.transform = move ? `translateY(${move}px)` : '';
    });
  });

  on('pointerup', '[data-doors-grip]', () => endSectionDrag(true));
  on('pointercancel', '[data-doors-grip]', () => endSectionDrag(false));

  /* Длина наполнения — сумма секций: они занимают его целиком, и второй
     раз считать её неоткуда, да и незачем. */
  const doorAlong = (door) => (door.sizes || []).reduce((sum, value) => sum + value, 0);

  on('click', '[data-doors-equal]', () => {
    const door = currentDoor();
    if (!door) return;
    const share = doorAlong(door) / door.sections.length;
    for (const section of door.sections) section.size = share;
    paintDoors();
  });

  on('click', '[data-doors-add]', () => {
    const door = currentDoor();
    if (!door || door.sections.length >= MAX_SECTIONS) return;

    /* Новая секция делит пополам последнюю, а не отнимает у всех: «поправил
       одну — поехали все» читается как поломка. Обе половины записываем
       числом — наполнение от прибавления секции не выросло. */
    const at = door.sections.length - 1;
    const half = (door.sizes ? door.sizes[at] : 0) / 2;
    if (half < MIN_SECTION) return;
    door.sections[at].size = half;
    door.sections.push({fill: door.sections[at].fill, size: half});
    activeSection = door.sections.length - 1;
    paintDoors();
  });

  on('click', '[data-doors-remove]', (_event, button) => {
    const door = currentDoor();
    const row = button.closest('[data-doors-section]');
    if (!door || !row || door.sections.length < 2) return;

    /* Место убранной секции достаётся соседу сверху, а у самой верхней —
       нижнему: наполнение не уменьшилось, и оставить дыру нельзя. */
    const at = Number(row.dataset.doorsIndex) || 0;
    const sizes = door.sizes || [];
    const heir = at > 0 ? at - 1 : 1;
    if (door.sections[heir]) door.sections[heir].size = (sizes[heir] || 0) + (sizes[at] || 0);
    door.sections.splice(at, 1);
    activeSection = Math.min(activeSection, door.sections.length - 1);
    paintDoors();
  });

  /* Пока печатают, слушаем каждый знак: соседняя секция ужимается на глазах,
     и видно, откуда взялось место. Но недобор не трогаем — в «1240» первой
     приходит единица, и на ней сосед забирал бы всю дверь.

     Уходя с поля, ставим окончательное: «5» превращается в минимум, а не
     остаётся висеть числом, по которому не считали. */
  function typeSectionSize(field, settle) {
    const door = currentDoor();
    const row = field.closest('[data-doors-section]');
    if (!door || !row) return;
    const at = Number(row.dataset.doorsIndex) || 0;
    const value = Number(field.value);
    if (!Number.isFinite(value) || (!settle && value < MIN_SECTION)) return;
    resizeSection(door, at, value, doorAlong(door));
    paintDoors();
    /* Просили больше, чем поместилось, — показываем, сколько вышло. Список
       поле под курсором не перебивает, а Enter курсор не уводит. */
    if (settle && door.sizes) field.value = String(Math.round(door.sizes[at]));
  }

  on('input', '[data-doors-section-size]', (_event, field) => typeSectionSize(field, false));
  on('change', '[data-doors-section-size]', (_event, field) => typeSectionSize(field, true));

  /* --------------------------------------------------- отслеживание посылки

     Перевозчик узнаётся по виду номера — никаких запросов наружу. Форматы
     эстонского рынка: JJFI + цифры — SmartPosti (бывш. Itella), S10
     с окончанием EE — Omniva, S10 с окончанием LT — Unisend, 11 или 14 цифр —
     DPD. Прочий S10 — входящая международная почта, её в Эстонии довозит
     Omniva. Venipak сюда намеренно не попал: у него в ходу и 8–9 цифр,
     и тринадцать знаков вида XX123456789XX — первое ни от чего не отличается,
     второе не отличить от S10. Ошибиться уверенно хуже, чем не угадать вовсе. */

  const CARRIERS = {
    omniva: {label: 'Omniva', url: (n) => `https://minu.omniva.ee/track/${encodeURIComponent(n)}`},
    smartposti: {
      label: 'SmartPosti',
      url: (n) => `https://www.smartposti.ee/saadetise-jalgimine/${encodeURIComponent(n)}`,
    },
    dpd: {
      label: 'DPD',
      url: (n) => `https://www.dpdgroup.com/ee/mydpd/my-parcels/search?parcelNumber=${encodeURIComponent(n)}`,
    },
    venipak: {
      label: 'Venipak',
      url: (n) => `https://venipak.com/lt/en/parcel-tracking/?vp_code=${encodeURIComponent(n)}`,
    },
    unisend: {label: 'Unisend', url: (n) => `https://my.unisend.ee/track/${encodeURIComponent(n)}`},
  };

  /* Пробелы и дефисы в наклейках ставят для читаемости, перевозчику они мешают. */
  const normaliseTracking = (raw) => raw.replace(/[\s-]+/g, '').toUpperCase();

  function detectCarrier(raw) {
    const number = normaliseTracking(raw);
    if (/^JJFI\d{14,20}$/.test(number)) return 'smartposti';
    if (/^[A-Z]{2}\d{9}EE$/.test(number)) return 'omniva';
    if (/^[A-Z]{2}\d{9}LT$/.test(number)) return 'unisend';
    if (/^[A-Z]{2}\d{9}[A-Z]{2}$/.test(number)) return 'omniva';
    if (/^\d{11}$|^\d{14}$/.test(number)) return 'dpd';
    return null;
  }

  on('input', '[data-tracking-input]', (event, field) => {
    const box = field.closest('[data-tracking]');
    if (!box) return;

    const strings = (() => {
      const node = $('[data-tracking-strings]');
      try {
        return node ? JSON.parse(node.textContent) : null;
      } catch {
        return null;
      }
    })();

    const hint = box.querySelector('[data-tracking-hint]');
    const open = box.querySelector('[data-tracking-open]');
    const number = normaliseTracking(field.value);

    if (!number) {
      if (hint) hint.textContent = '';
      if (open) open.hidden = true;
      return;
    }

    // Ссылки в общем списке тоже получают номер: покупатель, чей перевозчик
    // не угадался, не должен вводить его второй раз уже на чужом сайте.
    $$('[data-carrier]', box).forEach((link) => {
      const carrier = CARRIERS[link.dataset.carrier];
      if (carrier) link.href = carrier.url(number);
    });

    const id = detectCarrier(number);
    if (!id) {
      if (hint) hint.textContent = strings ? strings.unknown : '';
      if (open) open.hidden = true;
      return;
    }

    const carrier = CARRIERS[id];
    if (hint) hint.textContent = strings ? strings.guess.replace('%CARRIER%', carrier.label) : '';
    if (open) {
      open.hidden = false;
      open.href = carrier.url(number);
      open.textContent = strings ? strings.open.replace('%CARRIER%', carrier.label) : carrier.label;
    }
  });

  /* ------------------------------------------------------------- заставка

     Замок держим в углу и неярким — это служебная дверь, а не призыв
     к действию. Окно с полем открывается по нему и по ошибке пароля:
     после неудачной попытки страница приезжает заново, и прятать поле,
     в которое только что ошиблись, было бы издевательством. */

  on('click', '[data-password-open]', (event) => {
    event.preventDefault();
    const dialog = $('[data-password-dialog]');
    if (!dialog) return;
    dialog.hidden = false;
    dialog.querySelector('input')?.focus();
  });

  on('click', '[data-password-close]', (event) => {
    event.preventDefault();
    const dialog = $('[data-password-dialog]');
    if (dialog) dialog.hidden = true;
  });

  /* ------------------------------------------------------- согласие на куки

     Баннер рисует сама Shopify: он приходит с её скриптом, знает про регионы
     и, в отличие от самодельного, действительно гасит аналитические куки,
     а не только прячется. Своего у темы больше нет — два баннера сразу
     покупателю не объяснить. Вид чужому задан в theme.css.

     Нам остаётся одно: ссылка в подвале. Согласие по закону надо не только
     собрать, но и дать отозвать так же просто, как оно давалось, — а сам
     баннер после ответа не возвращается. Окно настроек открывает
     privacyBanner.showPreferences(): это опубликованный вход, и он же
     умеет показать окно тому, кто уже ответил. */

  on('click', '[data-cookie-settings]', (event) => {
    event.preventDefault();
    window.privacyBanner?.showPreferences?.();
  });

  /* --------------------------------------------------------------- запуск

     repaint() приводит в порядок то, что живёт на клиенте поверх серверной
     разметки, и зовётся заново каждый раз, когда разметку подменили, —
     при смене варианта, при смене фильтров и в редакторе темы. Его
     единственная обязанность — быть безобидным при повторном вызове.

     Отдельного init() больше нет: всё, что вешается один раз, — это
     обработчики выше, а они регистрируются при загрузке файла. Раньше init()
     существовал ради баннера согласия, который ставил слушателей на документ;
     баннер теперь рисует Shopify, и вешать нечего. */

  function repaint() {
    paintWishlist();
    $$('[data-quantity-picker]').forEach(paintQuantity);
    initDoorFamily();
    paintDoors();
    watchBuyBar();
    paintFilterColumn();
    paintCollectionView();
    $$('[data-price-range]').forEach(paintPrice);
  }

  /* ------------------------------------------------------------- отзывы

     Приглашение написать первый отзыв рисует тема, а форму — Judge.me:
     своей у витрины нет и быть не может, отзыв уходит к ним. Поэтому наша
     кнопка не открывает ничего сама, а переадресует нажатие настоящей
     кнопке виджета — она лежит рядом, в спрятанной шапке приложения.

     Ищем в момент нажатия, а не заранее: виджет — приложение на Vue,
     оно дорисовывает шапку после загрузки страницы и пересобирает её при
     фильтрации, так что заранее взятая ссылка протухнет. */

  on('click', '[data-review-open]', () => {
    const widget = $('[data-testid="write-review-button"]');
    if (widget) widget.click();
  });

  /* ------------------------------------------- подписка на возврат товара

     В плитке каталога «Уведомить» — обычная ссылка на карточку товара:
     спросить почту в плитке негде, форму держит приложение STOQ, и живёт
     она в блоке покупки. Но человек, нажавший «уведомить», уже сказал,
     чего хочет, — и приводить его на карточку, чтобы он искал там вторую
     такую же кнопку, значит спросить дважды. Поэтому ссылка приходит
     с хвостом #notify, а здесь он выполняется.

     Кнопку приходится дожидаться: приложение дорисовывает её после
     загрузки страницы. Ожидание ограничено пятью секундами — если за это
     время кнопки не появилось, значит товар доступен или приложение не
     ответило, и открывать нечего. Молчаливый вечный опрос хуже, чем
     ничего: он ничего не чинит, но продолжает жечь батарею. */

  function openNotifyForm() {
    if (window.location.hash !== '#notify') return;

    // Хвост убираем сразу: он своё отработал, и обновление страницы
    // не должно открывать окно заново.
    history.replaceState(null, '', window.location.pathname + window.location.search);

    const deadline = Date.now() + 5000;
    const tick = () => {
      const button = $('.restock-rocket-button-product');
      if (button) {
        button.click();
        return;
      }
      if (Date.now() < deadline) setTimeout(tick, 150);
    };
    tick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', repaint);
  } else {
    repaint();
  }

  /* Просьбу открыть окно подписки выполняем один раз за переход, а не при
     каждой перерисовке: repaint() зовётся и при смене варианта, и в
     редакторе темы, и окно выскакивало бы снова. */
  openNotifyForm();

  // Секции корзины и товара приезжают заново — перекрашиваем то, что живёт
  // на клиенте.
  document.addEventListener('shopify:section:load', repaint);
})();
